import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigation } from '../navigation';
import SalesHistoryStats from './sales-history-stats';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// Utility function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

const SalesHistory: React.FC = () => {
  const { navigateTo } = useNavigation();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedSale, setSelectedSale] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [customFromDateInput, setCustomFromDateInput] = useState('');
  const [customToDateInput, setCustomToDateInput] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewMode, setViewMode] = useState<'completed' | 'pending'>('completed');
  const [isSettlementOpen, setIsSettlementOpen] = useState(false);
  const [settlementSaleId, setSettlementSaleId] = useState<number | null>(null);
  const [settlementPendingAmount, setSettlementPendingAmount] = useState<number>(0);
  const [settlementAmount, setSettlementAmount] = useState<string>('');
  const [settlementMode, setSettlementMode] = useState<string>('upi');
  const [settlementNotes, setSettlementNotes] = useState<string>('');
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementError, setSettlementError] = useState<string | null>(null);
  const [settlementSuccess, setSettlementSuccess] = useState<string | null>(null);
  
  // WhatsApp states
  const [isSendingToWhatsApp, setIsSendingToWhatsApp] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [whatsAppResponseMessage, setWhatsAppResponseMessage] = useState<string | null>(null);
  const [showWhatsAppResponseMessage, setShowWhatsAppResponseMessage] = useState(false);
  const whatsAppPendingBlobRef = useRef<Blob | null>(null);
  const [currentWhatsAppSale, setCurrentWhatsAppSale] = useState<any | null>(null);
  const [showMobileInputModal, setShowMobileInputModal] = useState(false);
  const [mobileInputValue, setMobileInputValue] = useState('');
  const [mobileInputError, setMobileInputError] = useState<string | null>(null);
  const [currentWhatsAppMobile, setCurrentWhatsAppMobile] = useState<string>('');
  
  // API states
  const [salesData, setSalesData] = useState<any[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Medications states
  const [medicationsData, setMedicationsData] = useState<{[key: number]: any[]}>({});
  const [loadingMedications, setLoadingMedications] = useState<{[key: number]: boolean}>({});
  const [medicationsError, setMedicationsError] = useState<{[key: number]: string}>({});
  const fetchingMedicationsRef = useRef<{[key: number]: boolean}>({});
  const [billSummaryData, setBillSummaryData] = useState<{[key: number]: any}>({});
  const [analyticsData, setAnalyticsData] = useState<any | null>(null);
  const [salesTaxReport, setSalesTaxReport] = useState<any | null>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [csvDownloadError, setCsvDownloadError] = useState<string | null>(null);
  const [activeCalendarPicker, setActiveCalendarPicker] = useState<string | null>(null);
  const fromDatePickerMainRef = useRef<HTMLInputElement | null>(null);
  const toDatePickerMainRef = useRef<HTMLInputElement | null>(null);
  const fromDatePickerInlineRef = useRef<HTMLInputElement | null>(null);
  const toDatePickerInlineRef = useRef<HTMLInputElement | null>(null);

  const isFullDisplayDateValue = (value: string): boolean => /^\d{2}-\d{2}-\d{4}$/.test(value);

  const toDisplayDate = (ymd: string): string => {
    if (!ymd) return '';
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
    if (!match) return ymd;
    return `${match[3]}-${match[2]}-${match[1]}`;
  };

  const toYMDDate = (displayDate: string): string => {
    if (!displayDate) return '';
    const value = displayDate.trim();
    const dmyMatch = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
    if (dmyMatch) {
      return `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
    }
    const ymdMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (ymdMatch) {
      return value;
    }
    return '';
  };

  const normalizeDateTyping = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
  };

  const applyCalendarDate = (kind: 'from' | 'to', ymdValue: string) => {
    if (!ymdValue) return;
    const displayValue = toDisplayDate(ymdValue);
    if (kind === 'from') {
      setCustomFromDateInput(displayValue);
      commitFromDateIfComplete(displayValue);
      return;
    }
    setCustomToDateInput(displayValue);
    commitToDateIfComplete(displayValue);
  };

  const toggleCalendarPicker = (pickerKey: string, input: HTMLInputElement | null) => {
    if (!input) return;
    if (activeCalendarPicker === pickerKey) {
      input.blur();
      setActiveCalendarPicker(null);
      return;
    }
    setActiveCalendarPicker(pickerKey);
    input.focus();
    if (typeof (input as any).showPicker === 'function') {
      (input as any).showPicker();
    } else {
      input.click();
    }
  };

  const commitFromDateIfComplete = (value: string) => {
    const nextValue = value.trim();
    if (nextValue === '') {
      if (customFromDate !== '') {
        setCustomFromDate('');
        if (currentPage !== 1) setCurrentPage(1);
      }
      return;
    }
    if (!isFullDisplayDateValue(nextValue)) return;
    const nextYmd = toYMDDate(nextValue);
    if (!nextYmd || nextYmd === customFromDate) return;
    setCustomFromDate(nextYmd);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const commitToDateIfComplete = (value: string) => {
    const nextValue = value.trim();
    if (nextValue === '') {
      if (customToDate !== '') {
        setCustomToDate('');
        if (currentPage !== 1) setCurrentPage(1);
      }
      return;
    }
    if (!isFullDisplayDateValue(nextValue)) return;
    const nextYmd = toYMDDate(nextValue);
    if (!nextYmd || nextYmd === customToDate) return;
    setCustomToDate(nextYmd);
    if (currentPage !== 1) setCurrentPage(1);
  };

  // Fetch sales data from API with pagination
  const fetchSalesData = async (page: number = currentPage, perPage: number = itemsPerPage) => {
    try {
      setIsLoadingData(true);
      setApiError(null);
      
      // Get auth token from localStorage
      const authToken = getAuthToken();
      
      if (!authToken) {
        setApiError('Authentication token not found. Please log in again.');
        setIsLoadingData(false);
        return;
      }
      
       const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString()
      });
      const { from, to } = getEffectiveDatesSales();
      params.set('from_date', from);
      params.set('to_date', to);
      params.set('sort_by', 'created_at');
      params.set('order', sortOrder);
      const apiUrl = viewMode === 'pending'
        ? `https://pharma-amd-0-1-42.onrender.com/pharma/api/sales/confirm-pending?${params.toString()}`
        : `https://pharma-amd-0-1-42.onrender.com/pharma/api/sales/?${params.toString()}`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      if (!response.ok) {
        if (response.status === 500) {
          const params2 = new URLSearchParams({
            page: page.toString(),
            per_page: perPage.toString(),
            sort_by: 'invoice_date',
            order: sortOrder
          });
          const { from: start, to: end } = getEffectiveDatesSales();
          params2.set('start_date', start);
          params2.set('end_date', end);
          const apiUrl2 = viewMode === 'pending'
            ? `https://pharma-amd-0-1-42.onrender.com/pharma/api/sales/confirm-pending?${params2.toString()}`
            : `https://pharma-amd-0-1-42.onrender.com/pharma/api/sales/?${params2.toString()}`;
          const response2 = await fetch(apiUrl2, {
            method: 'GET',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
              'ngrok-skip-browser-warning': 'true',
              ...(authToken && { 'Authorization': `Bearer ${authToken}` })
            }
          });
          if (!response2.ok) {
            let detail2 = '';
            try { detail2 = await response2.text(); } catch {}
            throw new Error(`HTTP ${response.status} on from/to; fallback HTTP ${response2.status} on start/end${detail2 ? `: ${detail2}` : ''}`);
          }
          const data2 = await response2.json();
          if (data2 && Array.isArray(data2)) {
            setSalesData(data2);
            setTotalPages(1);
            setTotalItems(data2.length);
          } else if (data2 && data2.items && Array.isArray(data2.items)) {
            setSalesData(data2.items);
            setTotalPages(data2.pages || data2.total_pages || data2.totalPages || Math.ceil((data2.total || data2.items.length) / perPage) || 1);
            setTotalItems(data2.total || data2.items.length);
          } else if (data2 && data2.sales && Array.isArray(data2.sales)) {
            setSalesData(data2.sales);
            setTotalPages(data2.pages || data2.total_pages || data2.totalPages || Math.ceil((data2.total || data2.sales.length) / perPage) || 1);
            setTotalItems(data2.total || data2.sales.length);
          } else if (data2 && Array.isArray(data2.data)) {
            setSalesData(data2.data);
            setTotalPages(data2.pages || data2.total_pages || data2.totalPages || Math.ceil((data2.total || data2.data.length) / perPage) || 1);
            setTotalItems(data2.total || data2.data.length);
          } else {
            setSalesData([]);
            setTotalPages(1);
            setTotalItems(0);
          }
        } else {
          let detail = '';
          try {
            detail = await response.text();
          } catch {}
          throw new Error(`HTTP ${response.status}${detail ? `: ${detail}` : ''}`);
        }
      } else {
        const data = await response.json();
        if (data && Array.isArray(data)) {
          setSalesData(data);
          setTotalPages(1);
          setTotalItems(data.length);
        } else if (data && data.items && Array.isArray(data.items)) {
          setSalesData(data.items);
          setTotalPages(data.pages || data.total_pages || data.totalPages || Math.ceil((data.total || data.items.length) / perPage) || 1);
          setTotalItems(data.total || data.items.length);
        } else if (data && data.sales && Array.isArray(data.sales)) {
          setSalesData(data.sales);
          setTotalPages(data.pages || data.total_pages || data.totalPages || Math.ceil((data.total || data.sales.length) / perPage) || 1);
          setTotalItems(data.total || data.sales.length);
        } else if (data && Array.isArray(data.data)) {
          setSalesData(data.data);
          setTotalPages(data.pages || data.total_pages || data.totalPages || Math.ceil((data.total || data.data.length) / perPage) || 1);
          setTotalItems(data.total || data.data.length);
        } else {
          setSalesData([]);
          setTotalPages(1);
          setTotalItems(0);
        }
      }
      
    } catch (error: any) {
      
      // Enhanced error handling
      if (error?.message?.includes('CORS') || error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('preflight request') || error?.message?.includes('ERR_FAILED')) {
        setApiError(`CORS Error: Unable to connect to the sales API.

This error occurs because the server doesn't allow requests from localhost:3000.

Possible solutions:
1. Server needs CORS headers: Access-Control-Allow-Origin: http://localhost:3000
2. Check if the server is running and accessible
3. Verify the API endpoint URL is correct
4. Contact the server administrator to fix CORS configuration

Server endpoint: https://pharma-amd-0-1-42.onrender.com/pharma/api/sales/`);
      } else if (error?.message?.includes('ERR_CERT_COMMON_NAME_INVALID') || 
                 error?.message?.includes('ERR_CERT_AUTHORITY_INVALID') ||
                 error?.message?.includes('ERR_SSL_PROTOCOL_ERROR') ||
                 error?.message?.includes('certificate')) {
        setApiError('SSL Certificate Error: The server certificate is invalid. Please contact the administrator or try using HTTP instead of HTTPS.');
      } else if (error?.message?.includes('401') || error?.message?.includes('Unauthorized')) {
        setApiError('Authentication Error: Invalid or expired token. Please log in again.');
      } else if (error?.message?.includes('403') || error?.message?.includes('Forbidden')) {
        setApiError('Authorization Error: You do not have permission to access sales data.');
      } else {
        setApiError(`Network error: ${error?.message || 'Unknown error'}. Please check your connection and try again.`);
      }
    } finally {
      setIsLoadingData(false);
    }
  };

  // Fetch medications for a specific sale
  const fetchMedicationsForSale = async (saleId: number) => {
    // Prevent multiple simultaneous calls for the same sale
    if (fetchingMedicationsRef.current[saleId] || loadingMedications[saleId]) {
      return;
    }

    try {
      // Mark as fetching to prevent duplicate calls
      fetchingMedicationsRef.current[saleId] = true;
      
      // Set loading state for this specific sale
      setLoadingMedications(prev => ({ ...prev, [saleId]: true }));
      setMedicationsError(prev => ({ ...prev, [saleId]: '' }));
      
      // Get auth token from localStorage
      const authToken = getAuthToken();
      
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/sales/${saleId}/bill`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `Server error (Status: ${response.status})`;
        try {
          const errorText = await response.text();
          if (errorText) {
            try {
              const errorJson = JSON.parse(errorText);
              errorMessage = errorJson.error || errorJson.message || errorText || errorMessage;
            } catch {
              errorMessage = errorText || errorMessage;
            }
          }
        } catch (e) {
          // If we can't parse the error, use default message
        }
        
        if (response.status === 500) {
          errorMessage = 'Server error: Unable to fetch medications. Please try again later or contact support.';
        } else if (response.status === 404) {
          errorMessage = 'Medications not found for this sale.';
        } else if (response.status === 401 || response.status === 403) {
          errorMessage = 'Authentication error: Please log in again.';
        }
        
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Handle different response formats
      const medications = data.items || data.medications || data.bill_items || [];
      
      // Store medications data for this sale
      setMedicationsData(prev => ({ ...prev, [saleId]: medications }));
      // Store financial_summary if available, fallback to summary
      if (data.financial_summary) {
        setBillSummaryData(prev => ({ ...prev, [saleId]: data.financial_summary }));
      } else if (data.summary) {
        setBillSummaryData(prev => ({ ...prev, [saleId]: data.summary }));
      }
      
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to load medications. Please try again.';
      setMedicationsError(prev => ({ 
        ...prev, 
        [saleId]: errorMessage
      }));
    } finally {
      // Clear loading state and fetching flag for this specific sale
      fetchingMedicationsRef.current[saleId] = false;
      setLoadingMedications(prev => ({ ...prev, [saleId]: false }));
    }
  };

  const openSettlement = (sale: any) => {
    const totalAmount = sale.total_amount || sale.totalAmount || sale.amount || 0;
    const amountPaid = sale.amount_paid || sale.paid_amount || sale.paidAmount || sale.paid || 0;
    const totalNum = typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0;
    const paidNum = typeof amountPaid === 'number' ? amountPaid : parseFloat(amountPaid) || 0;
    const pending = Math.max(0, totalNum - paidNum);
    setSettlementSaleId(sale.id);
    setSettlementPendingAmount(pending);
    setSettlementAmount(pending.toFixed(2));
    setSettlementMode('upi');
    setSettlementNotes('');
    setSettlementError(null);
    setSettlementSuccess(null);
    setIsSettlementOpen(true);
  };

  const closeSettlement = () => {
    setIsSettlementOpen(false);
    setSettlementSaleId(null);
    setSettlementLoading(false);
    setSettlementError(null);
    setSettlementSuccess(null);
  };

  const submitSettlement = async () => {
    if (!settlementSaleId) return;
    try {
      setSettlementLoading(true);
      setSettlementError(null);
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      const body = {
        settlement_amount: Number(settlementAmount) || 0,
        payment_mode: settlementMode,
        payment_details: { txn_id: null, app: null },
        notes: settlementNotes?.trim() || null
      };
      const url = `https://pharma-amd-0-1-42.onrender.com/pharma/api/sales/confirm-pending/${settlementSaleId}/settlement`;
      const response = await fetch(url, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}`);
      }
      setSettlementSuccess('Payment settled successfully.');
      setIsSettlementOpen(false);
      setSettlementSaleId(null);
      fetchSalesData(currentPage, itemsPerPage);
    } catch (e: any) {
      setSettlementError(e?.message || 'Failed to settle payment.');
    } finally {
      setSettlementLoading(false);
    }
  };

  const formatYMD = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  /** Normalize to YYYY-MM-DD for API query params (e.g. CSV export). */
  const toYYYYMMDDQueryParam = (dateStr: string): string => {
    const v = dateStr.trim();
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const dmy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(v);
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
    return v;
  };

  const toDDMMYYYY = (ymd: string) => {
    if (!ymd) return '';
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (match) {
      return `${match[3]}-${match[2]}-${match[1]}`;
    }
    return ymd;
  };

  const getEffectiveDatesAnalytics = () => {
    const today = new Date();
    const todayDDMMYYYY = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
    const from = customFromDate ? toDDMMYYYY(customFromDate) : '01-01-2000';
    const to = customToDate ? toDDMMYYYY(customToDate) : todayDDMMYYYY;
    return { from, to };
  };
  
  const getEffectiveDatesSales = () => {
    const today = new Date();
    const todayYMD = formatYMD(today);
    const from = customFromDate ? customFromDate : '2000-01-01';
    const to = customToDate ? customToDate : todayYMD;
    return { from, to };
  };

  const applyDatePreset = (days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - (days - 1));
    setCustomFromDate(formatYMD(from));
    setCustomToDate(formatYMD(to));
    if (currentPage !== 1) setCurrentPage(1);
  };

  useEffect(() => {
    setCustomFromDateInput(toDisplayDate(customFromDate));
  }, [customFromDate]);

  useEffect(() => {
    setCustomToDateInput(toDisplayDate(customToDate));
  }, [customToDate]);

  const fetchAnalytics = async () => {
    try {
      setIsLoadingAnalytics(true);
      setAnalyticsError(null);
      const authToken = getAuthToken();
      if (!authToken) {
        setAnalyticsError('Authentication token not found. Please log in again.');
        setIsLoadingAnalytics(false);
        return;
      }
      const { from, to } = getEffectiveDatesAnalytics();
      const params = new URLSearchParams({
        from_date: from,
        to_date: to
      });
      const apiUrl = `https://pharma-amd-0-1-42.onrender.com/pharma/api/reporting/analytics/sales?${params.toString()}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setAnalyticsData(data);
    } catch (error: any) {
      setAnalyticsError(error?.message || 'Failed to load analytics.');
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  const fetchSalesTaxReport = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        setSalesTaxReport(null);
        return;
      }
      const { from, to } = getEffectiveDatesAnalytics();
      const params = new URLSearchParams({
        from_date: from,
        to_date: to
      });
      const apiUrl = `https://pharma-amd-0-1-42.onrender.com/pharma/api/reporting/tax/sales?${params.toString()}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSalesTaxReport(data);
    } catch {
      setSalesTaxReport(null);
    }
  };

  const downloadSalesOrdersCsv = async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      setCsvDownloadError('Please log in again to download.');
      setTimeout(() => setCsvDownloadError(null), 4000);
      return;
    }
    setCsvDownloadError(null);
    setIsDownloadingCsv(true);
    try {
      const { from, to } = getEffectiveDatesSales();
      const params = new URLSearchParams({
        from_date: toYYYYMMDDQueryParam(from),
        to_date: toYYYYMMDDQueryParam(to)
      });
      const response = await fetch(
        `https://pharma-amd-0-1-42.onrender.com/pharma/api/sales/sales_orders/csv?${params.toString()}`,
        {
        method: 'GET',
        mode: 'cors',
        headers: {
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        }
        }
      );
      if (!response.ok) {
        let detail = '';
        try {
          detail = await response.text();
        } catch { /* ignore */ }
        throw new Error(detail || `Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const cd = response.headers.get('Content-Disposition');
      let filename = 'sales_orders.csv';
      if (cd) {
        const m = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;\s]+)/i.exec(cd);
        if (m) {
          const raw = (m[1] || m[2] || m[3] || '').trim();
          if (raw) filename = decodeURIComponent(raw.replace(/^["']|["']$/g, ''));
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Could not download CSV';
      setCsvDownloadError(message);
      setTimeout(() => setCsvDownloadError(null), 5000);
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  // WhatsApp helper functions
  const runPresignedAndSendWhatsApp = async (pdfBlob: Blob, sale: any, mobileNumber: string) => {
    const customerId = localStorage.getItem('userId') ?? '';
    const presignedBody = { customer_id: String(customerId), file_name: 'test.pdf' };
    console.log('[WhatsApp] Step 1 — API: sales/whatsapp/presigned-upload, request body:', presignedBody);
    
    const presignedResponse = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/sales/whatsapp/presigned-upload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(presignedBody)
    });
    
    if (!presignedResponse.ok) {
      const errorText = await presignedResponse.text();
      let errorMessage = errorText;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.detail || errorJson.error || errorJson.message || errorText;
      } catch (e) {}
      console.error('[WhatsApp] Step 1 — sales/whatsapp/presigned-upload failed:', presignedResponse.status, errorMessage);
      throw new Error(`Failed to get presigned URL: ${errorMessage}`);
    }
    
    const presignedData = await presignedResponse.json();
    const { upload_url, s3_key } = presignedData;
    console.log('[WhatsApp] Step 1 — sales/whatsapp/presigned-upload response:', { ...presignedData, upload_url: upload_url ? '(present)' : undefined });
    if (!upload_url || !s3_key) throw new Error('Invalid response from presigned upload API');

    console.log('[WhatsApp] Step 2 — S3 PUT upload (PDF), s3_key:', s3_key, 'blob size:', pdfBlob.size);
    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/pdf' },
      body: pdfBlob,
    });
    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      console.error('[WhatsApp] Step 2 — S3 upload failed:', uploadResponse.status, errorText || uploadResponse.statusText);
      throw new Error(`Failed to upload PDF to S3: ${uploadResponse.status} ${errorText || uploadResponse.statusText}`);
    }
    console.log('[WhatsApp] Step 2 — S3 upload success, status:', uploadResponse.status);

    // Prepare form-data for send-whatsapp-document API
    const formData = new FormData();
    // Use the passed mobile number parameter
    const recipientMobile = mobileNumber || '';
    const customerName = sale?.customer_name || sale?.patient_name || sale?.patientName || '';
    formData.append('to', recipientMobile);
    formData.append('uploaded_file_key', s3_key);
    formData.append('patient_name', customerName?.trim() || '');
    formData.append('customer_id', customerId);
    
    // Get pharma_id from localStorage
    let pharmaId = '';
    try {
      const pharmaStr = localStorage.getItem('pharmaDetails');
      if (pharmaStr) {
        const pharma = JSON.parse(pharmaStr);
        pharmaId = pharma.id || pharma.pharma_id || '';
      }
    } catch (_) {}
    formData.append('pharma_id', pharmaId);

    // Validate mobile number before proceeding
    if (!recipientMobile || recipientMobile.trim() === '') {
      throw new Error('Mobile number is missing. Please try again.');
    }

    console.log('[WhatsApp] Step 3 — API: sales/send-whatsapp-document (FormData), uploaded_file_key:', s3_key, 'customer_id:', customerId, 'mobileNumber:', recipientMobile);
    
    // Debug: Log FormData contents
    console.log('[WhatsApp] FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(`  ${key}: ${value}`);
    }
    const sendUrl = 'https://pharma-amd-0-1-42.onrender.com/pharma/api/sales/send-whatsapp-document';
    const authToken = getAuthToken();
    const processResponse = await fetch(sendUrl, {
      method: 'POST',
      headers: {
        'ngrok-skip-browser-warning': 'true',
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
      body: formData,
    });
    if (!processResponse.ok) {
      const errorText = await processResponse.text();
      let errorMessage = errorText;
      try {
        const errorJson = JSON.parse(errorText);
        const detail = errorJson.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map((err: { loc?: string[]; msg?: string }) =>
            (err.loc && err.loc.length ? err.loc.join('.') + ': ' : '') + (err.msg || JSON.stringify(err))
          ).join('; ');
        } else {
          errorMessage = detail || errorJson.error || errorJson.message || errorText;
        }
      } catch (e) {}
      console.error('[WhatsApp] Step 3 — sales/send-whatsapp-document failed:', processResponse.status, errorMessage);
      throw new Error(`Failed to send to WhatsApp: ${errorMessage}`);
    }
    const processResult = await processResponse.json().catch(() => ({}));
    console.log('[WhatsApp] Step 3 — sales/send-whatsapp-document response:', processResult);
    console.log('[WhatsApp] Process complete — invoice sent to WhatsApp successfully');
    setWhatsAppResponseMessage('Invoice sent to WhatsApp successfully!');
    setShowWhatsAppResponseMessage(true);
    setTimeout(() => setShowWhatsAppResponseMessage(false), 5000);
  };

  // Handle invoice printing
  const handlePrintInvoice = async (saleId: number) => {
    try {
      const authToken = getAuthToken();
      
      if (!authToken) {
        alert('Authentication token not found. Please log in again.');
        return;
      }

      // Fetch invoice data
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/sales/${saleId}/bill`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch invoice data: ${response.status}`);
      }

      const invoiceData = await response.json();
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        alert('Please allow popups for this website to print invoices.');
        return;
      }

      // Generate invoice HTML
      const invoiceHTML = generateInvoiceHTML(invoiceData, saleId);
      
      printWindow.document.write(invoiceHTML);
      printWindow.document.close();
      
      // Wait for content to load, then print
      printWindow.onload = () => {
        printWindow.print();
      };

    } catch (error: any) {
      console.error('Error printing invoice:', error);
      alert('Failed to print invoice. Please try again.');
    }
  };

  // Handle WhatsApp sending
  const handleSendToWhatsApp = async (sale: any) => {
    const customerPhone = sale?.customer_phone || sale?.patient_phone || sale?.phone || '';
    
    if (!customerPhone || customerPhone.trim() === '') {
      // Show mobile input modal instead of alert
      setCurrentWhatsAppSale(sale);
      setMobileInputValue('');
      setMobileInputError(null);
      setShowMobileInputModal(true);
      return;
    }

    // Proceed with existing phone number
    await proceedWithWhatsAppFlow(customerPhone, sale);
  };

  // Handle OTP submission
  const handleOtpSubmit = async () => {
    const otp = otpValue.trim();
    if (!otp) {
      setOtpError('Please enter the OTP.');
      return;
    }
    if (!currentWhatsAppSale) {
      setOtpError('Sale information not found. Please try again.');
      return;
    }
    
    setIsVerifyingOtp(true);
    setOtpError(null);
    try {
      // Use the stored mobile number instead of getting it from the sale
      const number = currentWhatsAppMobile;
      
      if (!number) {
        setOtpError('Mobile number not found. Please try again.');
        return;
      }
      
      // Get pharmacy ID for verification
      let pharmaId = '';
      try {
        const pharmaStr = localStorage.getItem('pharmaDetails');
        if (pharmaStr) {
          const pharma = JSON.parse(pharmaStr);
          pharmaId = pharma.id || pharma.pharma_id || '';
        }
      } catch (_) {}
      
      const verifyBody = {
        number: number,
        otp: otp,
        is_pharma: true,
        pharma_id: pharmaId
      };
      
      const url = 'https://edit-agent-amd-0-1-15.onrender.com/real-agent/whatsapp/verify-otp';
      console.log('[WhatsApp] Verify OTP — API: verify-otp, request body:', { ...verifyBody, otp: '***' });
      
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify(verifyBody),
      });
      
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.detail || data?.message || data?.error || res.statusText || 'Verification failed';
        setOtpError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        return;
      }
      
      console.log('[WhatsApp] OTP verification success');
      setShowOtpModal(false);
      setOtpValue('');
      const pendingBlob = whatsAppPendingBlobRef.current;
      whatsAppPendingBlobRef.current = null;
      
      if (pendingBlob && currentWhatsAppSale) {
        setIsSendingToWhatsApp(true);
        try {
          // Re-check verification status after OTP verification
          console.log('[WhatsApp] Re-checking verification status after OTP success');
          const reCheckResponse = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/auth/whatsapp/verified-check', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'ngrok-skip-browser-warning': 'true',
              'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify({ mobile: currentWhatsAppMobile }),
          });
          
          const reCheckData = reCheckResponse.ok ? await reCheckResponse.json().catch(() => ({})) : null;
          console.log('[WhatsApp] Re-check verification status:', reCheckData);
          
          const isNowVerified = reCheckData?.verified === true;
          
          if (!isNowVerified) {
            throw new Error('Mobile number is still not verified after OTP. Please try again.');
          }
          
          await runPresignedAndSendWhatsApp(pendingBlob, currentWhatsAppSale, currentWhatsAppMobile);
        } catch (err: any) {
          setWhatsAppResponseMessage(err?.message || 'Failed to send to WhatsApp.');
          setShowWhatsAppResponseMessage(true);
          setTimeout(() => setShowWhatsAppResponseMessage(false), 8000);
        } finally {
          setIsSendingToWhatsApp(false);
        }
      }
    } catch (err: any) {
      setOtpError(err?.message || 'Verification request failed.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Handle OTP resend
  const handleResendOtp = async () => {
    if (!currentWhatsAppSale) {
      setOtpError('Sale information not found. Please try again.');
      return;
    }
    
    setIsResendingOtp(true);
    setOtpError(null);
    try {
      // Use the stored mobile number instead of getting it from the sale
      const mobile = currentWhatsAppMobile;
      
      if (!mobile) {
        setOtpError('Mobile number not found. Please try again.');
        return;
      }
      
      // Get pharmacy details for verification
      let pharmaName = '';
      let pharmaId = '';
      try {
        // Use full_name from localStorage as pharma_name
        const fullName = localStorage.getItem('full_name');
        pharmaName = fullName || '';
        
        const pharmaStr = localStorage.getItem('pharmaDetails');
        if (pharmaStr) {
          const pharma = JSON.parse(pharmaStr);
          pharmaId = pharma.id || pharma.pharma_id || '';
        }
      } catch (_) {}
      
      // Request number verification again
      const requestVerificationBody = {
        number: mobile,
        name: currentWhatsAppSale?.customer_name || currentWhatsAppSale?.patient_name || currentWhatsAppSale?.patientName || '',
        is_pharma: true,
        pharma_name: pharmaName,
        pharma_id: pharmaId
      };
      
      console.log('[WhatsApp] Resend OTP — API: number-verification, request body:', requestVerificationBody);
      const authToken = getAuthToken();
      const verificationResponse = await fetch('https://edit-agent-amd-0-1-15.onrender.com/real-agent/whatsapp/number-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
        body: JSON.stringify(requestVerificationBody),
      });
      
      if (!verificationResponse.ok) {
        const errorText = await verificationResponse.text();
        console.error('[WhatsApp] Resend OTP failed:', verificationResponse.status, errorText);
        setOtpError('Failed to resend OTP. Please try again.');
        return;
      }
      
      console.log('[WhatsApp] OTP resent successfully');
      setOtpError(null);
      setWhatsAppResponseMessage('OTP has been resent to your phone number.');
      setShowWhatsAppResponseMessage(true);
      setTimeout(() => setShowWhatsAppResponseMessage(false), 5000);
    } catch (error: any) {
      console.error('Error resending OTP:', error);
      setOtpError(error?.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsResendingOtp(false);
    }
  };

  // Handle mobile number submission from input modal
  const handleMobileNumberSubmit = async () => {
    const mobile = mobileInputValue.trim().replace(/\s+|[-()]/g, '');
    
    if (!mobile) {
      setMobileInputError('Please enter a mobile number');
      return;
    }
    
    if (mobile.length < 10) {
      setMobileInputError('Please enter a valid mobile number (at least 10 digits)');
      return;
    }
    
    setMobileInputError(null);
    setShowMobileInputModal(false);
    
    // Proceed with WhatsApp flow using the entered mobile number
    await proceedWithWhatsAppFlow(mobile, currentWhatsAppSale);
  };

  // Modified WhatsApp flow that accepts a mobile number parameter
  const proceedWithWhatsAppFlow = async (mobileNumber: string, sale: any) => {
    console.log('[WhatsApp] Send to WhatsApp clicked — starting process');
    try {
      setIsSendingToWhatsApp(true);

      // Fetch invoice data first
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error('Authentication token not found.');
      }

      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/sales/${sale.id}/bill`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch invoice data: ${response.status}`);
      }

      const invoiceData = await response.json();
      
      // Generate invoice HTML and capture as PDF
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = generateInvoiceHTML(invoiceData, sale.id);
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = '210mm';
      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: tempDiv.scrollWidth,
        height: tempDiv.scrollHeight,
      });

      document.body.removeChild(tempDiv);

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Create PDF with A4 dimensions
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);
      
      const scaleX = contentWidth / (imgWidth * 0.264583);
      const scaleY = contentHeight / (imgHeight * 0.264583);
      const scale = Math.min(scaleX, scaleY);
      
      const scaledWidth = imgWidth * scale * 0.264583;
      const scaledHeight = imgHeight * scale * 0.264583;
      
      const x = (pageWidth - scaledWidth) / 2;
      const y = margin;
      
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      pdf.addImage(imgData, 'JPEG', x, y, scaledWidth, scaledHeight);
      const pdfBlob = pdf.output('blob');

      (async () => {
        const blob = pdfBlob;
        if (!blob) {
          alert('Failed to capture invoice as PDF.');
          setIsSendingToWhatsApp(false);
          return;
        }
        console.log('[WhatsApp] Invoice captured as PDF, blob size:', blob.size, 'bytes');

        try {
          // Step 0: Check WhatsApp verification
          const rawPhone = mobileNumber.trim().replace(/\s+|[-()]/g, '');
          const mobile = rawPhone.startsWith('91') ? rawPhone : (rawPhone.length <= 10 ? `91${rawPhone}` : rawPhone);
          
          // Store the current mobile number for OTP verification
          setCurrentWhatsAppMobile(mobile);
          
          console.log('[WhatsApp] Step 0 — API: whatsapp/verified-check, mobile:', mobile);
          const checkResponse = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/auth/whatsapp/verified-check', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'ngrok-skip-browser-warning': 'true',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({ mobile }),
          });
          
          const checkData = checkResponse.ok ? await checkResponse.json().catch(() => ({})) : null;
          console.log('[WhatsApp] Step 0 — whatsapp/verified-check response:', checkData);
          
          const isVerified = checkData?.verified === true;

          if (isVerified) {
            console.log('[WhatsApp] Step 0 — phone number verified, proceeding with upload and send');
            await runPresignedAndSendWhatsApp(blob, sale, mobile);
          } else {
            console.log('[WhatsApp] Step 0 — phone number not verified, requesting number verification');
            whatsAppPendingBlobRef.current = blob;
            
            // Get pharmacy details for verification
            let pharmaName = '';
            let pharmaId = '';
            try {
              // Use full_name from localStorage as pharma_name
              const fullName = localStorage.getItem('full_name');
              pharmaName = fullName || '';
              
              const pharmaStr = localStorage.getItem('pharmaDetails');
              if (pharmaStr) {
                const pharma = JSON.parse(pharmaStr);
                pharmaId = pharma.id || pharma.pharma_id || '';
              }
            } catch (_) {}
            
            // Step 1: Request number verification
            const requestVerificationBody = {
              number: mobile,
              name: sale?.customer_name || sale?.patient_name || sale?.patientName || '',
              is_pharma: true,
              pharma_name: pharmaName,
              pharma_id: pharmaId
            };
            
            console.log('[WhatsApp] Request number verification — API: number-verification, request body:', requestVerificationBody);
            const verificationResponse = await fetch('https://edit-agent-amd-0-1-15.onrender.com/real-agent/whatsapp/number-verification', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'ngrok-skip-browser-warning': 'true',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify(requestVerificationBody),
            });
            
            if (!verificationResponse.ok) {
              const errorText = await verificationResponse.text();
              console.error('[WhatsApp] Request number verification failed:', verificationResponse.status, errorText);
              throw new Error(`Failed to request number verification: ${errorText}`);
            }
            
            console.log('[WhatsApp] Number verification requested successfully, showing OTP modal');
            setShowOtpModal(true);
            setOtpValue('');
            setOtpError(null);
          }
        } catch (error: any) {
          console.error('Error in WhatsApp flow:', error);
          setWhatsAppResponseMessage(error?.message || 'Failed to verify number or send to WhatsApp.');
          setShowWhatsAppResponseMessage(true);
          setTimeout(() => setShowWhatsAppResponseMessage(false), 8000);
        } finally {
          setIsSendingToWhatsApp(false);
        }
      })();
    } catch (error: any) {
      console.error('Error capturing invoice:', error);
      alert('Failed to capture invoice as PDF. Please try again.');
      setIsSendingToWhatsApp(false);
    }
  };

  // Generate invoice HTML for printing - Matching Billing-summary.tsx design
  const generateInvoiceHTML = (invoiceData: any, saleId: number) => {
    const sale = salesData.find(s => s.id === saleId);
    const medications = invoiceData.items || invoiceData.medications || invoiceData.bill_items || [];
    
    // Get financial_summary from invoiceData (preferred) or fallback to summary
    const financialSummary = invoiceData.financial_summary || invoiceData.summary || {};
    
    // Get clinic info from localStorage (same as Billing-summary.tsx)
    let clinicInfo = null;
    let fullName = null;
    let gstin = null;
    let dlNo = null;
    
    try {
      const stored = localStorage.getItem('clinicDetails') || localStorage.getItem('pharmacyDetails');
      if (stored) {
        const parsed = JSON.parse(stored);
        clinicInfo = {
          name: parsed.name || parsed.clinic_name || parsed.pharmacy_name,
          address: parsed.address || parsed.clinic_address || parsed.pharmacy_address,
          phone: parsed.phone || parsed.mobile || parsed.contact_number,
          gstNumber: parsed.gstNumber || parsed.gst_no || parsed.gst || parsed.gstin,
        };
      }
    } catch (e) {
      console.error('Failed to load clinic details from localStorage', e);
    }
    
    try {
      fullName = localStorage.getItem('full_name');
      gstin = localStorage.getItem('gstin');
      dlNo = localStorage.getItem('dl_no');
    } catch (e) {
      console.error('Failed to load additional details from localStorage', e);
    }
    
    // Extract customer info from invoiceData.customer or fallback to sale object
    const customer = invoiceData.customer || {};
    const customerName = customer.name || sale?.customer_name || sale?.patient_name || sale?.patientName || 'Unknown Customer';
    const customerPhone = customer.phone || sale?.customer_phone || sale?.patient_phone || sale?.phone || 'N/A';
    const customerAddress = customer.address || sale?.address || null;
    
    // Extract invoice info from invoiceData or fallback to sale object
    const invoiceNo = invoiceData.invoice_no || sale?.invoice_no || sale?.invoiceNo || sale?.id || 'N/A';
    const saleDate = invoiceData.invoice_date || invoiceData.created_at || sale?.invoice_date || sale?.created_at || sale?.sale_date || sale?.saleDate || new Date().toISOString();
    
    // Use financial_summary values, fallback to sale object or calculated values
    const grandTotal = financialSummary.total_amount ?? financialSummary.total ?? sale?.total_amount ?? sale?.totalAmount ?? sale?.amount ?? 0;
    const amountPaid = financialSummary.amount_paid ?? sale?.amount_paid ?? sale?.paid_amount ?? sale?.paidAmount ?? sale?.paid ?? 0;
    const balanceDue = financialSummary.balance_due ?? (grandTotal - amountPaid);
    const paymentMode = financialSummary.payment_mode ?? sale?.payment_mode ?? sale?.paymentMode ?? 'cash';
    const status = financialSummary.status ?? (sale?.status || sale?.payment_status || 'pending');
    const regNo = sale?.patientId || (sale as any)?.reg_no || (sale as any)?.patient_id || null;
    const doctorName = sale?.doctor_name || (sale as any)?.doctor || (sale as any)?.doctorName || null;
    
    // Helper function to format expiry date (same as Billing-summary.tsx)
    const formatExpiryDate = (dateStr: string): string => {
      if (!dateStr) return '—';
      try {
        const date = new Date(dateStr);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]}-${date.getFullYear()}`;
      } catch {
        return dateStr;
      }
    };

    // Helper function to format currency (same as Billing-summary.tsx)
    const formatCurrency = (value: number): string => {
      return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Calculate totals from medications as fallback if financial_summary is not available
    const calculatedTotalAmount = medications.reduce((sum: number, item: any) => {
      const unitPrice = item.unit_price || item.selling_price || 0;
      const quantity = item.quantity || 0;
      const discountPercent = item.discount_percent || 0;
      const taxPercent = item.tax_percent ?? item.gst_rate ?? item.gst ?? 0;
      const grossAmount = unitPrice * quantity;
      const discountAmount = (grossAmount * discountPercent) / 100;
      const afterDiscount = grossAmount - discountAmount;
      const gstAmount = (afterDiscount * taxPercent) / 100;
      return sum + (afterDiscount + gstAmount);
    }, 0);

    // Use financial_summary values if available, otherwise use calculated values
    const finalTotalAmount = grandTotal || calculatedTotalAmount;

    // Bill totals aligned with Billing-summary.tsx (financial_summary + line fallbacks)
    const summaryItems = medications;
    const computedFromItems = summaryItems.reduce(
      (acc: any, item: any) => {
        const unitPrice = Number(item.unit_price ?? item.selling_price ?? 0);
        const quantity = Number(item.quantity ?? 0);
        const discountAmount = Number(item.line_discount_amount ?? item.discount_amount ?? 0);
        const cgst = Number(item.line_cgst_amount ?? item.cgst_amount ?? 0);
        const sgst = Number(item.line_sgst_amount ?? item.sgst_amount ?? 0);
        const taxAmount = Number(item.line_tax_amount ?? item.tax_amount ?? (cgst + sgst));
        const lineTotalRaw = item.line_total ?? item.final_amount;
        const lineTotal =
          lineTotalRaw !== undefined && lineTotalRaw !== null && lineTotalRaw !== ''
            ? Number(lineTotalRaw)
            : unitPrice * quantity - discountAmount + taxAmount;
        const lineSubtotal = lineTotal - taxAmount;
        acc.subtotal += lineSubtotal;
        acc.lineDiscount += discountAmount;
        acc.lineTax += taxAmount;
        acc.total += lineTotal;
        return acc;
      },
      { subtotal: 0, lineDiscount: 0, lineTax: 0, total: 0 }
    );
    const lineSumCgst = summaryItems.reduce(
      (s: number, item: any) => s + Number(item.line_cgst_amount ?? item.cgst_amount ?? 0),
      0
    );
    const lineSumSgst = summaryItems.reduce(
      (s: number, item: any) => s + Number(item.line_sgst_amount ?? item.sgst_amount ?? 0),
      0
    );
    const lineSumTax = summaryItems.reduce((s: number, item: any) => {
      const cgst = Number(item.line_cgst_amount ?? item.cgst_amount ?? 0);
      const sgst = Number(item.line_sgst_amount ?? item.sgst_amount ?? 0);
      return s + Number(item.line_tax_amount ?? item.tax_amount ?? (cgst + sgst));
    }, 0);
    const grossBeforeDiscountFromItems = summaryItems.reduce(
      (acc: number, item: any) =>
        acc + Number(item.unit_price ?? item.selling_price ?? 0) * Number(item.quantity ?? 0),
      0
    );
    const billSubtotalWithoutTax = Number(
      financialSummary.subtotal ?? computedFromItems.subtotal ?? 0
    );
    const billLineDiscount = Number(
      financialSummary.line_discount_amount ?? computedFromItems.lineDiscount ?? 0
    );
    const billOverallTax = Number(
      financialSummary.overall_tax_amount ??
        financialSummary.line_tax_amount ??
        lineSumTax ??
        computedFromItems.lineTax ??
        0
    );
    const billOverallCgst = Number(
      financialSummary.overall_cgst_amount ?? lineSumCgst
    );
    const billOverallSgst = Number(
      financialSummary.overall_sgst_amount ?? lineSumSgst
    );
    const billSubtotalAmount = Number(
      financialSummary.subtotal_amount ??
        (grossBeforeDiscountFromItems > 0
          ? grossBeforeDiscountFromItems
          : financialSummary.subtotal ?? computedFromItems.subtotal)
    );
    const billTotalAmount = Number(
      financialSummary.total_amount ??
        financialSummary.total ??
        computedFromItems.total ??
        finalTotalAmount
    );
    const billAmountPaid = Number(amountPaid ?? 0);
    const billBalanceDue = Number(
      financialSummary.balance_due ?? Math.max(billTotalAmount - billAmountPaid, 0)
    );

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice #${invoiceNo}</title>
        <style>
          @page {
            margin: 8mm;
            size: A4;
          }
          body { 
            font-family: 'Arial', 'Helvetica', sans-serif;
            margin: 0;
            padding: 0;
            color: #000000;
            background: #ffffff;
            font-size: 11px;
            line-height: 1.3;
          }
          *,
          *::before,
          *::after {
            box-sizing: border-box;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;
            margin: 0;
          }
          th, td {
            border: 1px solid #000000;
            padding: 4px 3px;
            font-size: 10px;
            line-height: 1.2;
          }
          th {
            background: #f0f0f0 !important;
            font-weight: 600;
          }
          h2 {
            margin: 0.3rem 0;
            font-size: 1.3rem;
            line-height: 1.2;
          }
          h3 {
            margin: 0.2rem 0;
            font-size: 0.9rem;
          }
          p {
            margin: 0.2rem 0;
            font-size: 10px;
            line-height: 1.3;
          }
          div {
            page-break-inside: avoid;
          }
          @media print {
            body {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <div style="padding: 1.25rem 1.5rem; width: 100%; max-width: 100%; box-sizing: border-box; font-size: 0.85rem; line-height: 1.4;">
          <!-- Pharmacy Header -->
          <div style="text-align: center; margin-bottom: 0.75rem;">
            <h2 style="font-size: 1.5rem; font-weight: bold; color: #111827; margin: 0 0 0.4rem 0; text-transform: uppercase; letter-spacing: 0.03em; line-height: 1.2;">
              ${fullName || clinicInfo?.name || 'PHARMACY BILL'}
            </h2>
            ${clinicInfo?.address ? `
              <div style="font-size: 0.75rem; color: #111827; line-height: 1.4; margin-bottom: 0.3rem;">
                ${clinicInfo.address}
              </div>
            ` : ''}
            ${clinicInfo?.phone ? `
              <div style="font-size: 0.75rem; color: #111827; margin-bottom: 0;">
                Ph no:- ${clinicInfo.phone}
              </div>
            ` : ''}
          </div>

          <!-- Key Identifiers Section -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem; padding-bottom: 0.5rem; border-bottom: 2px solid #111827; font-size: 0.75rem;">
            <div style="text-align: left; font-weight: 600; color: #111827;">
              DL NO :- ${dlNo || 'N/A'}
            </div>
            <div style="text-align: center; font-weight: 700; color: #111827; font-size: 0.9rem; text-transform: uppercase;">
              Sale Bill
            </div>
            <div style="text-align: right; font-weight: 600; color: #111827;">
              GST NO :- ${gstin || clinicInfo?.gstNumber || 'N/A'}
            </div>
          </div>

          <!-- Patient and Bill Details -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 0.75rem; font-size: 0.75rem; border-bottom: 1px solid #111827; padding-bottom: 0.5rem;">
            <div>
              <div style="margin-bottom: 0.3rem;">
                <strong>Patient Name:</strong> ${customerName}
              </div>
              ${customerAddress ? `
              <div style="margin-bottom: 0.3rem;">
                <strong>Address:</strong> ${customerAddress}
              </div>
              ` : ''}
              ${regNo ? `
              <div style="margin-bottom: 0.3rem;">
                <strong>Reg No:</strong> ${regNo}
              </div>
              ` : ``}
              ${doctorName ? `
              <div style="margin-bottom: 0;">
                <strong>Doctor:</strong> ${doctorName}
              </div>
              ` : ``}
            </div>
            <div>
              <div style="margin-bottom: 0.3rem;">
                <strong>Bill No:</strong> ${invoiceNo}
              </div>
              <div style="margin-bottom: 0.3rem;">
                <strong>Mobile:</strong> ${customerPhone && customerPhone !== 'N/A' && customerPhone.trim() !== '' ? `+91 ${customerPhone}` : 'N/A'}
              </div>
              <div style="margin-bottom: 0;">
                <strong>Date:</strong> ${new Date(saleDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </div>
            </div>
          </div>

          <!-- Items Table -->
          <div style="margin-bottom: 0.75rem; overflow-x: auto;">
            <table style="width: 100%; border-collapse: collapse; font-size: 0.7rem; font-family: inherit; border: 1px solid #111827;">
              <thead>
                <tr style="background: #f9fafb; color: #111827; font-weight: 600; border-bottom: 2px solid #111827;">
                  <th style="padding: 0.4rem 0.3rem; text-align: left; border: 1px solid #111827; font-size: 0.7rem;">Particular (s)</th>
                  <th style="padding: 0.4rem 0.3rem; text-align: center; border: 1px solid #111827; font-size: 0.7rem;">Batch No</th>
                  <th style="padding: 0.4rem 0.3rem; text-align: center; border: 1px solid #111827; font-size: 0.7rem;">Exp Date</th>
                  <th style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; font-size: 0.7rem;">Rate</th>
                  <th style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; font-size: 0.7rem;">Qty</th>
                  <th style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; font-size: 0.7rem;">Amount</th>
                  <th style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; font-size: 0.7rem;">Gross(Rs)</th>
                  <th style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; font-size: 0.7rem;">CGST(Rs)</th>
                  <th style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; font-size: 0.7rem;">SGST(Rs)</th>
                  <th style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; font-size: 0.7rem;">Final Amount(Rs)</th>
                </tr>
              </thead>
              <tbody>
                ${medications.map((item: any, index: number) => {
                  const unitPrice = Number(item.unit_price || item.selling_price || 0);
                  const quantity = Number(item.quantity || 0);
                  const rateTimesQty = unitPrice * quantity;
                  
                  // Use API-provided line-level amounts if available, otherwise calculate
                  const lineDiscountAmount = item.line_discount_amount ?? item.discount_amount ?? 0;
                  const cgstFromItem = Number(item.line_cgst_amount ?? 0);
                  const sgstFromItem = Number(item.line_sgst_amount ?? 0);
                  const lineTaxAmount = Number(
                    item.line_tax_amount ??
                      item.tax_amount ??
                      (cgstFromItem || sgstFromItem ? cgstFromItem + sgstFromItem : 0)
                  );
                  const lineCgstAmount = item.line_cgst_amount != null ? cgstFromItem : lineTaxAmount / 2;
                  const lineSgstAmount = item.line_sgst_amount != null ? sgstFromItem : lineTaxAmount / 2;
                  const lineTotalRaw = item.line_total ?? item.final_amount;
                  const lineTotal =
                    lineTotalRaw !== undefined && lineTotalRaw !== null && lineTotalRaw !== ''
                      ? Number(lineTotalRaw)
                      : 0;
                  
                  // Calculate gross amount (before tax and discount) — fallback when API line_total missing
                  const baseGrossAmount = rateTimesQty;
                  const grossAfterDiscount = baseGrossAmount - lineDiscountAmount;
                  // Bill Gross(Rs): line total minus tax (matches invoice breakdown)
                  const hasLineTotal =
                    lineTotalRaw !== undefined && lineTotalRaw !== null && lineTotalRaw !== '';
                  const grossAmount = hasLineTotal
                    ? Math.round((lineTotal - lineTaxAmount) * 100) / 100
                    : grossAfterDiscount;
                  
                  return `
                    <tr style="background-color: ${index % 2 === 0 ? 'white' : '#f9fafb'}; border-bottom: 1px solid #111827;">
                      <td style="padding: 0.4rem 0.3rem; border: 1px solid #111827; color: #111827; font-size: 0.7rem;">
                        ${item.medicine_name || item.name || 'N/A'}
                      </td>
                      <td style="padding: 0.4rem 0.3rem; text-align: center; border: 1px solid #111827; color: #111827; font-size: 0.7rem;">
                        ${item.batch_no || '—'}
                      </td>
                      <td style="padding: 0.4rem 0.3rem; text-align: center; border: 1px solid #111827; color: #111827; font-size: 0.7rem;">
                        ${item.expiry_date || item.exp_date ? formatExpiryDate(item.expiry_date || item.exp_date) : '—'}
                      </td>
                      <td style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; color: #111827; font-size: 0.7rem;">
                        ${unitPrice.toFixed(2)}
                      </td>
                      <td style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; color: #111827; font-size: 0.7rem;">
                        ${quantity}
                      </td>
                      <td style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; color: #111827; font-size: 0.7rem;">
                        ${rateTimesQty.toFixed(2)}
                      </td>
                      <td style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; color: #111827; font-size: 0.7rem;">
                        ${grossAmount.toFixed(2)}
                      </td>
                      <td style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; color: #111827; font-size: 0.7rem;">
                        ${lineCgstAmount.toFixed(2)}
                      </td>
                      <td style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; color: #111827; font-size: 0.7rem;">
                        ${lineSgstAmount.toFixed(2)}
                      </td>
                      <td style="padding: 0.4rem 0.3rem; text-align: right; border: 1px solid #111827; color: #111827; font-weight: 600; font-size: 0.7rem;">
                        ${lineTotal.toFixed(2)}
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>

          <!-- Financial Summary Section (aligned with Billing-summary.tsx) -->
          <div style="display: flex; justify-content: flex-end; margin-top: 0.5rem;">
            <div style="width: 280px; font-size: 0.7rem; border: 1px solid #111827; padding: 0.6rem; background: #f9fafb;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 0.3rem;">
                <span><strong>Subtotal Amount:</strong></span>
                <span><strong>${formatCurrency(billSubtotalAmount)}</strong></span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 0.3rem;">
                <span><strong>Discount:</strong></span>
                <span><strong>${formatCurrency(billLineDiscount)}</strong></span>
              </div>
              <div style="margin-top: 0.35rem; margin-bottom: 0.35rem; padding: 0.45rem 0.4rem; border: 1px solid #d1d5db; border-radius: 4px; background: #f3f4f6;">
                <div style="font-weight: 700; margin-bottom: 0.35rem; font-size: 0.68rem; color: #374151;">Tax summary</div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 0.25rem;">
                  <span><strong>Subtotal (without tax):</strong></span>
                  <span><strong>${formatCurrency(billSubtotalWithoutTax)}</strong></span>
                </div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 0.25rem;">
                  <span><strong>Overall Tax Amount:</strong></span>
                  <span><strong>${formatCurrency(billOverallTax)}</strong></span>
                </div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 0.25rem;">
                  <span><strong>Overall CGST Amount:</strong></span>
                  <span><strong>${formatCurrency(billOverallCgst)}</strong></span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span><strong>Overall SGST Amount:</strong></span>
                  <span><strong>${formatCurrency(billOverallSgst)}</strong></span>
                </div>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 0.3rem; border-top: 1px solid #111827; padding-top: 0.35rem;">
                <span><strong>Grand Total:</strong></span>
                <span><strong>${formatCurrency(billTotalAmount)}</strong></span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 0.3rem;">
                <span><strong>Paid Amount:</strong></span>
                <span><strong>${formatCurrency(billAmountPaid)}</strong></span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 0.3rem;">
                <span><strong>Due:</strong></span>
                <span><strong>${formatCurrency(billBalanceDue)}</strong></span>
              </div>
              <div style="margin-top: 0.5rem; padding-top: 0.5rem; border-top: 1px solid #111827;">
                <div style="margin-bottom: 0.3rem; font-size: 0.7rem;">
                  <strong>Paid Amount (In Words):</strong>
                </div>
                <div style="font-size: 0.65rem; font-style: italic; text-transform: uppercase; line-height: 1.3;">
                  ${amountPaid} RUPEES
                </div>
              </div>
              <div style="margin-top: 0.5rem; padding-top: 0.5rem; border-top: 1px solid #111827;">
                <div style="display: flex; justify-content: space-between;">
                  <span><strong>Pay Mode:</strong></span>
                  <span><strong>${paymentMode.toUpperCase()}</strong></span>
                </div>
                <div style="display: flex; justify-content: space-between; margin-top: 0.3rem;">
                  <span><strong>Status:</strong></span>
                  <span><strong>${String(status || 'completed').toUpperCase()}</strong></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div style="margin-top: 1rem; display: flex; justify-content: space-between; align-items: flex-start; font-size: 0.7rem; color: #111827; border-top: 1px solid #111827; padding-top: 0.6rem;">
            <div style="flex: 1;">
              <div style="margin-bottom: 0.4rem; font-weight: 600; font-size: 0.7rem;">
                Note: Fridge items once sold cannot be taken back.
              </div>
              <div style="margin-top: 0.65rem; padding: 0.55rem 0.6rem; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; font-size: 0.65rem; line-height: 1.45; color: #374151;">
                <div style="font-weight: 700; margin-bottom: 0.35rem; color: #111827; font-size: 0.68rem;">Column definitions (line items)</div>
                <ul style="margin: 0; padding-left: 1.1rem;">
                  <li style="margin-bottom: 0.25rem;"><strong>Amt. (Rs)</strong> — &quot;Amount&quot;: rate × quantity. Shown <em>before</em> any line discount.</li>
                  <li style="margin-bottom: 0.25rem;"><strong>Final Amt. (Rs)</strong> — Line total <em>after</em> discount is applied and <em>after</em> CGST/SGST (the full payable amount for that line on this bill).</li>
                  <li style="margin-bottom: 0.25rem;"><strong>Gross (Rs)</strong> — Value <em>after</em> discount and <em>before</em> tax (pre‑tax line amount).</li>
                  <li><strong>Tax</strong> — CGST/SGST is calculated on the post‑discount amount (the same basis as <strong>Gross</strong>), not on undiscounted <strong>Amt.</strong></li>
                </ul>
              </div>
              ${fullName ? `
                <div style="margin-top: 0.6rem; font-size: 0.7rem;">
                  <strong>Printed By :</strong> ${fullName}
                </div>
              ` : ''}
            </div>
            <div style="text-align: right; min-width: 120px;">
              <div style="margin-top: 2rem; border-top: 1px solid #111827; padding-top: 0.4rem; font-size: 0.7rem;">
                <strong>Authorized Signature</strong>
              </div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };


  const isDateRangeReady = useMemo(() => {
    const hasFrom = Boolean(customFromDate);
    const hasTo = Boolean(customToDate);
    return (hasFrom && hasTo) || (!hasFrom && !hasTo);
  }, [customFromDate, customToDate]);

  // Fetch sales data on component mount and when pagination/date/view changes
  useEffect(() => {
    if (!isDateRangeReady) return;
    const t = setTimeout(() => {
      fetchSalesData(currentPage, itemsPerPage);
    }, 200);
    return () => clearTimeout(t);
  }, [currentPage, itemsPerPage, customFromDate, customToDate, viewMode, sortOrder, isDateRangeReady]);

  useEffect(() => {
    if (!isDateRangeReady) return;
    fetchAnalytics();
    fetchSalesTaxReport();
  }, [customFromDate, customToDate, isDateRangeReady]);

  // Trigger load animation
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter sales data based on search and filters - Memoized to prevent recalculation on every render
  const filteredSales = useMemo(() => {
    return salesData.filter(sale => {
      // Handle different possible field names from API - Updated to match actual response
      const customerName = sale.customer_name || sale.patient_name || sale.patientName || '';
      const customerPhone = sale.customer_phone || sale.patient_phone || sale.phone || '';
      const invoiceNo = sale.invoice_no || sale.invoiceNo || sale.id || '';
      const saleDate = sale.invoice_date || sale.created_at || sale.sale_date || sale.saleDate || '';
      const status = sale.status || sale.payment_status || 'pending';
      
      const matchesSearch = searchTerm === '' || 
        customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customerPhone.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        saleDate.includes(searchTerm);
      
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      
      const matchesDate = dateFilter === 'all' || 
        (dateFilter === 'today' && saleDate === new Date().toISOString().split('T')[0]) ||
        (dateFilter === 'week' && new Date(saleDate) >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) ||
        (dateFilter === 'month' && new Date(saleDate) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
      
      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [salesData, searchTerm, statusFilter, dateFilter]);

  // Calculate pagination - Server-side pagination, so use filteredSales directly
  const totalFilteredItems = useMemo(() => filteredSales.length, [filteredSales]);
  const startIndex = useMemo(() => Math.max(0, (currentPage - 1) * itemsPerPage), [currentPage, itemsPerPage]);
  const endIndex = useMemo(() => Math.min(totalItems, startIndex + itemsPerPage), [totalItems, startIndex, itemsPerPage]);
  
  // Use filteredSales directly since API handles pagination, but we still filter client-side for search/filters
  const paginatedSales = useMemo(() => {
    // If we have server-side pagination, use filteredSales directly (already paginated by API)
    // Otherwise, apply client-side pagination
    if (totalItems > 0 && totalItems !== filteredSales.length) {
      // Server-side pagination is active, use filteredSales as-is
      return filteredSales;
    } else {
      // Client-side pagination fallback
      return totalFilteredItems > 0 ? filteredSales.slice(startIndex, endIndex) : [];
    }
  }, [filteredSales, totalFilteredItems, startIndex, endIndex, totalItems]);

  // Update total pages when API response changes
  useEffect(() => {
    // Reset to page 1 if current page is beyond available pages or if no results
    if ((currentPage > totalPages || (totalItems === 0 && currentPage > 1)) && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [totalPages, totalItems, currentPage]);

  // Memoize status color functions to prevent recreation on every render
  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'paid': return '#10b981';
      case 'partial': return '#f59e0b';
      case 'pending': return '#ef4444';
      default: return '#6b7280';
    }
  }, []);

  const getStatusBgColor = useCallback((status: string) => {
    switch (status) {
      case 'completed': return '#ecfdf5';
      case 'paid': return '#ecfdf5';
      case 'partial': return '#fffbeb';
      case 'pending': return '#fef2f2';
      default: return '#f9fafb';
    }
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '0',
      position: 'relative',
      overflow: 'hidden',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Enhanced Animated Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 20%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 40% 60%, rgba(245, 158, 11, 0.06) 0%, transparent 50%),
          radial-gradient(circle at 60% 30%, rgba(139, 92, 246, 0.05) 0%, transparent 50%),
          radial-gradient(circle at 10% 70%, rgba(236, 72, 153, 0.04) 0%, transparent 50%)
        `,
        animation: 'backgroundFloat 25s ease-in-out infinite'
      }}></div>

      {/* Floating Particles */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        {/* Particle 1 */}
        <div style={{
          position: 'absolute',
          width: '8px',
          height: '8px',
          background: 'rgba(16, 185, 129, 0.3)',
          borderRadius: '50%',
          top: '20%',
          left: '10%',
          animation: 'particleFloat1 15s ease-in-out infinite'
        }}></div>
        
        {/* Particle 2 */}
        <div style={{
          position: 'absolute',
          width: '12px',
          height: '12px',
          background: 'rgba(59, 130, 246, 0.25)',
          borderRadius: '50%',
          top: '60%',
          left: '85%',
          animation: 'particleFloat2 18s ease-in-out infinite'
        }}></div>
        
        {/* Particle 3 */}
        <div style={{
          position: 'absolute',
          width: '6px',
          height: '6px',
          background: 'rgba(245, 158, 11, 0.4)',
          borderRadius: '50%',
          top: '40%',
          left: '70%',
          animation: 'particleFloat3 20s ease-in-out infinite'
        }}></div>
        
        {/* Particle 4 */}
        <div style={{
          position: 'absolute',
          width: '10px',
          height: '10px',
          background: 'rgba(139, 92, 246, 0.2)',
          borderRadius: '50%',
          top: '80%',
          left: '20%',
          animation: 'particleFloat4 16s ease-in-out infinite'
        }}></div>
        
        {/* Particle 5 */}
        <div style={{
          position: 'absolute',
          width: '14px',
          height: '14px',
          background: 'rgba(236, 72, 153, 0.15)',
          borderRadius: '50%',
          top: '10%',
          left: '60%',
          animation: 'particleFloat5 22s ease-in-out infinite'
        }}></div>
        
        {/* Particle 6 */}
        <div style={{
          position: 'absolute',
          width: '8px',
          height: '8px',
          background: 'rgba(16, 185, 129, 0.2)',
          borderRadius: '50%',
          top: '70%',
          left: '50%',
          animation: 'particleFloat6 19s ease-in-out infinite'
        }}></div>
      </div>

      {/* Geometric Shapes */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        {/* Triangle 1 */}
        <div style={{
          position: 'absolute',
          width: '0',
          height: '0',
          borderLeft: '15px solid transparent',
          borderRight: '15px solid transparent',
          borderBottom: '25px solid rgba(16, 185, 129, 0.1)',
          top: '15%',
          left: '25%',
          animation: 'shapeRotate1 30s linear infinite'
        }}></div>
        
        {/* Triangle 2 */}
        <div style={{
          position: 'absolute',
          width: '0',
          height: '0',
          borderLeft: '20px solid transparent',
          borderRight: '20px solid transparent',
          borderBottom: '35px solid rgba(59, 130, 246, 0.08)',
          top: '65%',
          left: '75%',
          animation: 'shapeRotate2 35s linear infinite'
        }}></div>
        
        {/* Circle */}
        <div style={{
          position: 'absolute',
          width: '30px',
          height: '30px',
          border: '2px solid rgba(245, 158, 11, 0.15)',
          borderRadius: '50%',
          top: '35%',
          left: '15%',
          animation: 'shapePulse 12s ease-in-out infinite'
        }}></div>
        
        {/* Square */}
        <div style={{
          position: 'absolute',
          width: '25px',
          height: '25px',
          border: '2px solid rgba(139, 92, 246, 0.12)',
          borderRadius: '4px',
          top: '55%',
          left: '80%',
          animation: 'shapeRotate3 25s linear infinite'
        }}></div>
      </div>

      {/* Content Container */}
      <div style={{ position: 'relative', zIndex: 10 }}>
        {/* Enhanced Modern Header */}
        <header className="sales-header" style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(25px)',
          borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
          padding: '1.75rem 0',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          transform: isLoaded ? 'translateY(0)' : 'translateY(-20px)',
          opacity: isLoaded ? 1 : 0,
          transition: 'all 0.6s ease-out',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}>
          <div className="sales-header-container" style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 2rem',
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            position: 'relative',
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}>
            {/* Back Button - Far Left */}
            <button
              onClick={() => navigateTo('pharma-homepage')}
              style={{
                background: 'linear-gradient(135deg, #1B5E20 0%, #0D4A14 100%)',
                color: 'white',
                border: 'none',
                padding: '0.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(27, 94, 32, 0.3)',
                transition: 'all 0.3s ease',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '0',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(27, 94, 32, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(27, 94, 32, 0.3)';
              }}
            >
              ←
            </button>

            {/* Center Heading - Absolutely Centered */}
            <div className="sales-header-title" style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1f2937',
              textAlign: 'center'
            }}>
              Sales History
            </div>

            {/* Search Bar - Right Side */}
            <div className="sales-search-container" style={{ 
              position: 'relative', 
              minWidth: isMobile ? '100%' : '300px',
              maxWidth: isMobile ? '100%' : 'none',
              width: isMobile ? '100%' : 'auto',
              marginLeft: isMobile ? '0' : 'auto',
              marginTop: isMobile ? '1rem' : '0',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}>
              <svg
                className="sales-search-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{
                  position: 'absolute',
                  left: isMobile ? '0.875rem' : '1.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  zIndex: 1,
                  pointerEvents: 'none'
                }}
              >
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search by customer name, phone, invoice number or date..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="sales-search-input"
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  padding: isMobile ? '0.875rem 0.875rem 0.875rem 2.5rem' : '1rem 1rem 1rem 3.5rem',
                  paddingLeft: isMobile ? '2.5rem' : '3.5rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: isMobile ? '12px' : '12px',
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  backgroundColor: 'white',
                  color: '#1f2937',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.boxShadow = '0 0 0 4px rgba(16, 185, 129, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                }}
              />
              {/* Clear Search Button */}
              {searchTerm && (
                <button
                  className="sales-clear-button"
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: isMobile ? '0.75rem' : '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    color: '#9ca3af',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    zIndex: 2
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#ef4444';
                    e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#9ca3af';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  title="Clear search"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '2rem',
          transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
          opacity: isLoaded ? 1 : 0,
          transition: 'all 0.6s ease-out 0.2s'
        }}>

          {/* Loading State */}
          {isLoadingData && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4rem',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              borderRadius: '20px',
              border: '1px solid rgba(16, 185, 129, 0.1)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
              marginBottom: '2rem'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid rgba(16, 185, 129, 0.1)',
                borderTop: '4px solid #10b981',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '1rem'
              }}></div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Loading Sales Data...
              </h3>
              <p style={{
                fontSize: '1rem',
                color: '#6b7280'
              }}>
                Please wait while we fetch your sales history
              </p>
            </div>
          )}

          {/* Error State */}
          {apiError && !isLoadingData && (
            <div style={{
              padding: '2rem',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              borderRadius: '16px',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              boxShadow: '0 8px 24px rgba(239, 68, 68, 0.1)',
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                color: 'white',
                fontSize: '1.5rem'
              }}>
                ⚠️
              </div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Unable to Load Sales Data
              </h3>
              <p style={{
                fontSize: '1rem',
                color: '#6b7280',
                marginBottom: '1rem',
                whiteSpace: 'pre-line'
              }}>
                {apiError}
              </p>
              <button
                onClick={() => fetchSalesData(currentPage, itemsPerPage)}
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                }}
              >
                Try Again
              </button>
            </div>
          )}

          {/* Content - Only show when not loading and no error */}
          {!isLoadingData && !apiError && (
            <>
              {/* Stats Overview */}
              <SalesHistoryStats
                salesData={salesData}
                analytics={analyticsData}
                salesTaxReport={salesTaxReport}
              />

              {false && (
              <section style={{ marginBottom: '2rem' }}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '12px',
                  padding: '1rem',
                  border: '1px solid rgba(16, 185, 129, 0.1)',
                  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
                  width: '100%',
                  maxWidth: '900px',
                  margin: '0 auto 1.5rem auto'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '0.75rem',
                    alignItems: 'end'
                  }}>
                    <div>
                      <label style={{
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#6b7280',
                        marginBottom: '0.4rem',
                        display: 'block'
                      }}>
                        Payment Status
                      </label>
                      <div style={{
                        display: 'inline-flex',
                        border: '2px solid #e5e7eb',
                        borderRadius: '9999px',
                        overflow: 'hidden',
                        background: 'white',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)'
                      }}>
                        <button
                          onClick={() => { setViewMode('completed'); setCurrentPage(1); }}
                          style={{
                            padding: '0.4rem 0.9rem',
                            border: 'none',
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            background: viewMode === 'completed' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'transparent',
                            color: viewMode === 'completed' ? 'white' : '#374151',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          ALL
                        </button>
                        <button
                          onClick={() => { setViewMode('pending'); setCurrentPage(1); }}
                          style={{
                            padding: '0.4rem 0.9rem',
                            border: 'none',
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            background: viewMode === 'pending' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : 'transparent',
                            color: viewMode === 'pending' ? 'white' : '#374151',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Pending
                        </button>
                      </div>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <h3 style={{
                        fontSize: '0.95rem',
                        fontWeight: '700',
                        color: '#374151',
                        margin: '0 0 0.25rem 0'
                      }}>
                        Select Date Range
                      </h3>
                    </div>
                    <div>
                      <label style={{
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#6b7280',
                        marginBottom: '0.4rem',
                        display: 'block'
                      }}>
                        From Date
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <input
                          type="text"
                          value={customFromDateInput}
                          onChange={(e) => {
                            const nextValue = normalizeDateTyping(e.target.value);
                            setCustomFromDateInput(nextValue);
                            commitFromDateIfComplete(nextValue);
                          }}
                          style={{
                            padding: '0.5rem 0.75rem',
                            border: '2px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            backgroundColor: 'white',
                            color: '#1f2937',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            minWidth: '150px'
                          }}
                          inputMode="numeric"
                          placeholder="DD-MM-YYYY"
                          maxLength={10}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#10b981';
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                          }}
                          onBlur={(e) => {
                            commitFromDateIfComplete(e.currentTarget.value);
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        />
                        <div style={{ position: 'relative', width: '34px', height: '34px' }}>
                          <button
                            type="button"
                            title="Pick From Date"
                            onClick={() => toggleCalendarPicker('main-from', fromDatePickerMainRef.current)}
                            style={{
                              width: '34px',
                              height: '34px',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              background: '#ffffff',
                              color: '#374151',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" />
                              <line x1="16" y1="2" x2="16" y2="6" />
                              <line x1="8" y1="2" x2="8" y2="6" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                          </button>
                          <input
                            type="date"
                            ref={fromDatePickerMainRef}
                            value={customFromDate}
                            onChange={(e) => {
                              applyCalendarDate('from', e.target.value);
                              setActiveCalendarPicker(null);
                            }}
                            onBlur={() => {
                              if (activeCalendarPicker === 'main-from') setActiveCalendarPicker(null);
                            }}
                            aria-label="Pick From Date"
                            style={{
                              position: 'absolute',
                              inset: 0,
                              opacity: 0,
                              pointerEvents: 'none'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label style={{
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#6b7280',
                        marginBottom: '0.4rem',
                        display: 'block'
                      }}>
                        To Date
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <input
                          type="text"
                          value={customToDateInput}
                          onChange={(e) => {
                            const nextValue = normalizeDateTyping(e.target.value);
                            setCustomToDateInput(nextValue);
                            commitToDateIfComplete(nextValue);
                          }}
                          style={{
                            padding: '0.5rem 0.75rem',
                            border: '2px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            backgroundColor: 'white',
                            color: '#1f2937',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            minWidth: '150px'
                          }}
                          inputMode="numeric"
                          placeholder="DD-MM-YYYY"
                          maxLength={10}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#10b981';
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                          }}
                          onBlur={(e) => {
                            commitToDateIfComplete(e.currentTarget.value);
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        />
                        <div style={{ position: 'relative', width: '34px', height: '34px' }}>
                          <button
                            type="button"
                            title="Pick To Date"
                            onClick={() => toggleCalendarPicker('main-to', toDatePickerMainRef.current)}
                            style={{
                              width: '34px',
                              height: '34px',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              background: '#ffffff',
                              color: '#374151',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" />
                              <line x1="16" y1="2" x2="16" y2="6" />
                              <line x1="8" y1="2" x2="8" y2="6" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                          </button>
                          <input
                            type="date"
                            ref={toDatePickerMainRef}
                            value={customToDate}
                            onChange={(e) => {
                              applyCalendarDate('to', e.target.value);
                              setActiveCalendarPicker(null);
                            }}
                            onBlur={() => {
                              if (activeCalendarPicker === 'main-to') setActiveCalendarPicker(null);
                            }}
                            aria-label="Pick To Date"
                            style={{
                              position: 'absolute',
                              inset: 0,
                              opacity: 0,
                              pointerEvents: 'none'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    marginTop: '0.75rem',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      onClick={() => applyDatePreset(3)}
                      style={{
                        padding: '0.35rem 0.6rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '9999px',
                        background: '#fff',
                        color: '#374151',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)'
                      }}
                    >
                      Last 3 Days
                    </button>
                    <button
                      onClick={() => applyDatePreset(7)}
                      style={{
                        padding: '0.35rem 0.6rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '9999px',
                        background: '#fff',
                        color: '#374151',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)'
                      }}
                    >
                      Last Week
                    </button>
                    <button
                      onClick={() => applyDatePreset(30)}
                      style={{
                        padding: '0.35rem 0.6rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '9999px',
                        background: '#fff',
                        color: '#374151',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)'
                      }}
                    >
                      Last Month
                    </button>
                  </div>
                </div>
              </section>
              )}

              {/* Sales List */}
          <section>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 19c-5 0-9-4-9-9s4-9 9-9 9 4 9 9-4 9-9 9zM21 3l-2 2m-7 7l2 2"/>
                </svg>
              </div>
              <h2 style={{
                fontSize: '1.3rem',
                fontWeight: '700',
                color: '#1f2937',
                margin: 0
              }}>
                Sales Records ({totalItems} total{totalItems > 0 ? `, showing ${paginatedSales.length} of ${totalItems}` : ''})
              </h2>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              gap: '1rem',
              marginBottom: '0.75rem',
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280' }}>Payment Status</span>
                <div style={{
                  display: 'inline-flex',
                  border: '2px solid #e5e7eb',
                  borderRadius: '9999px',
                  overflow: 'hidden',
                  background: 'white'
                }}>
                  <button
                    onClick={() => { setViewMode('completed'); setCurrentPage(1); }}
                    style={{
                      padding: '0.35rem 0.7rem',
                      border: 'none',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: viewMode === 'completed' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'transparent',
                      color: viewMode === 'completed' ? 'white' : '#374151',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    ALL
                  </button>
                  <button
                    onClick={() => { setViewMode('pending'); setCurrentPage(1); }}
                    style={{
                      padding: '0.35rem 0.7rem',
                      border: 'none',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: viewMode === 'pending' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : 'transparent',
                      color: viewMode === 'pending' ? 'white' : '#374151',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Pending
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280' }}>Order</span>
                  <select
                    value={sortOrder}
                    onChange={(e) => { setSortOrder(e.target.value as 'asc' | 'desc'); setCurrentPage(1); }}
                    style={{
                      padding: '0.4rem 0.6rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      backgroundColor: 'white',
                      color: '#1f2937',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      minWidth: '160px'
                    }}
                  >
                    <option value="desc">New to Old</option>
                    <option value="asc">Old to New</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-end' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280' }}>Select Date Range</span>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>From</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <input
                        type="text"
                        value={customFromDateInput}
                        onChange={(e) => {
                          const nextValue = normalizeDateTyping(e.target.value);
                          setCustomFromDateInput(nextValue);
                          commitFromDateIfComplete(nextValue);
                        }}
                        onBlur={(e) => commitFromDateIfComplete(e.currentTarget.value)}
                        style={{
                          padding: '0.45rem 0.65rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          backgroundColor: 'white',
                          color: '#1f2937',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          minWidth: '140px'
                        }}
                        inputMode="numeric"
                        placeholder="DD-MM-YYYY"
                        maxLength={10}
                      />
                      <div style={{ position: 'relative', width: '32px', height: '32px' }}>
                        <button
                          type="button"
                          title="Pick From Date"
                          onClick={() => toggleCalendarPicker('inline-from', fromDatePickerInlineRef.current)}
                          style={{
                            width: '32px',
                            height: '32px',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            background: '#ffffff',
                            color: '#374151',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                          </svg>
                        </button>
                        <input
                          type="date"
                          ref={fromDatePickerInlineRef}
                          value={customFromDate}
                          onChange={(e) => {
                            applyCalendarDate('from', e.target.value);
                            setActiveCalendarPicker(null);
                          }}
                          onBlur={() => {
                            if (activeCalendarPicker === 'inline-from') setActiveCalendarPicker(null);
                          }}
                          aria-label="Pick From Date"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0,
                            pointerEvents: 'none'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>To</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <input
                        type="text"
                        value={customToDateInput}
                        onChange={(e) => {
                          const nextValue = normalizeDateTyping(e.target.value);
                          setCustomToDateInput(nextValue);
                          commitToDateIfComplete(nextValue);
                        }}
                        onBlur={(e) => commitToDateIfComplete(e.currentTarget.value)}
                        style={{
                          padding: '0.45rem 0.65rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          backgroundColor: 'white',
                          color: '#1f2937',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          minWidth: '140px'
                        }}
                        inputMode="numeric"
                        placeholder="DD-MM-YYYY"
                        maxLength={10}
                      />
                      <div style={{ position: 'relative', width: '32px', height: '32px' }}>
                        <button
                          type="button"
                          title="Pick To Date"
                          onClick={() => toggleCalendarPicker('inline-to', toDatePickerInlineRef.current)}
                          style={{
                            width: '32px',
                            height: '32px',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            background: '#ffffff',
                            color: '#374151',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                          </svg>
                        </button>
                        <input
                          type="date"
                          ref={toDatePickerInlineRef}
                          value={customToDate}
                          onChange={(e) => {
                            applyCalendarDate('to', e.target.value);
                            setActiveCalendarPicker(null);
                          }}
                          onBlur={() => {
                            if (activeCalendarPicker === 'inline-to') setActiveCalendarPicker(null);
                          }}
                          aria-label="Pick To Date"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0,
                            pointerEvents: 'none'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', alignSelf: 'flex-end' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>Export</span>
                    <button
                      type="button"
                      onClick={downloadSalesOrdersCsv}
                      disabled={isDownloadingCsv}
                      title="Download sales orders for the selected date range as CSV"
                      style={{
                        padding: '0.45rem 0.75rem',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: isDownloadingCsv ? 'not-allowed' : 'pointer',
                        background: isDownloadingCsv
                          ? '#9ca3af'
                          : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isDownloadingCsv ? 'Downloading…' : 'Download CSV'}
                    </button>
                    {csvDownloadError && (
                      <span style={{
                        fontSize: '0.68rem',
                        color: '#dc2626',
                        lineHeight: 1.25,
                        maxWidth: 'min(260px, 85vw)'
                      }}>
                        {csvDownloadError}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => applyDatePreset(3)}
                    style={{
                      padding: '0.3rem 0.55rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '9999px',
                      background: '#fff',
                      color: '#374151',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Last 3 Days
                  </button>
                  <button
                    onClick={() => applyDatePreset(7)}
                    style={{
                      padding: '0.3rem 0.55rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '9999px',
                      background: '#fff',
                      color: '#374151',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Last Week
                  </button>
                  <button
                    onClick={() => applyDatePreset(30)}
                    style={{
                      padding: '0.3rem 0.55rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '9999px',
                      background: '#fff',
                      color: '#374151',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Last Month
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    style={{
                      padding: '0.3rem 0.55rem',
                      border: '1px solid #ef4444',
                      borderRadius: '8px',
                      background: '#fff',
                      color: '#ef4444',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Card Layout */}
            {isMobile ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                width: '100%',
                padding: '0 0.5rem'
              }}>
                {paginatedSales.map((sale, index) => {
                // Extract data with fallbacks for different API field names - Updated to match actual response
                const customerName = sale.customer_name || sale.patient_name || sale.patientName || 'Unknown Customer';
                const customerPhone = sale.customer_phone || sale.patient_phone || sale.phone || 'N/A';
                const invoiceNo = sale.invoice_no || sale.invoiceNo || sale.id || 'N/A';
                const saleDate = sale.invoice_date || sale.created_at || sale.sale_date || sale.saleDate || 'N/A';
                const status = sale.status || sale.payment_status || 'pending';
                const totalAmount = sale.total_amount || sale.totalAmount || sale.amount || 0;
                const amountPaid = sale.amount_paid || sale.paid_amount || sale.paidAmount || sale.paid || 0;
                const paymentMode = sale.payment_mode || sale.paymentMode || 'cash';
                const customerType = sale.customer_type || sale.customerType || 'individual';
                const notes = sale.notes || '';
                const createdBy = sale.created_by || sale.createdBy || 'N/A';

                return (
                  <div
                    key={sale.id}
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                      backdropFilter: 'blur(25px)',
                      borderRadius: '16px',
                      padding: '1.25rem',
                      border: '1px solid rgba(16, 185, 129, 0.1)',
                      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.06)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      animation: `slideInUp 0.4s ease-out ${index * 0.05}s both`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 10px 28px rgba(16, 185, 129, 0.12)';
                      e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.06)';
                      e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.1)';
                    }}
                  >
                    {/* Mobile: label left / value right; actions in one row */}
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '0.5rem',
                        marginBottom: '0.35rem'
                      }}>
                        <h3 style={{
                          fontSize: '1rem',
                          fontWeight: '700',
                          color: '#1f2937',
                          margin: 0,
                          flex: 1,
                          minWidth: 0,
                          lineHeight: 1.3
                        }}>
                          {customerName}
                        </h3>
                        <span style={{
                          background: '#f3f4f6',
                          color: '#6b7280',
                          padding: '0.2rem 0.4rem',
                          borderRadius: '4px',
                          fontSize: '0.65rem',
                          fontWeight: '600',
                          fontFamily: 'monospace',
                          flexShrink: 0
                        }}>
                          {invoiceNo}
                        </span>
                      </div>
                      <div style={{ width: '100%', borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: '0.25rem' }}>
                        {([
                          ['Date', saleDate],
                          ...(customerPhone && customerPhone !== 'N/A' ? [['Phone', `📞 ${customerPhone}`] as const] : []),
                          ['Payment', String(paymentMode).toUpperCase()],
                          ['Total', `₹${(typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0).toFixed(2)}`],
                          ['Paid', `₹${(typeof amountPaid === 'number' ? amountPaid : parseFloat(amountPaid) || 0).toFixed(2)}`]
                        ] as const).map(([label, val]) => (
                          <div
                            key={label}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.45rem 0',
                              borderBottom: '1px solid rgba(0,0,0,0.06)',
                              minWidth: 0
                            }}
                          >
                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600, flexShrink: 0 }}>{label}</span>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1f2937', textAlign: 'right', wordBreak: 'break-word' }}>{val}</span>
                          </div>
                        ))}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.45rem 0',
                          borderBottom: '1px solid rgba(0,0,0,0.06)'
                        }}>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>Status</span>
                          <span style={{
                            background: getStatusBgColor(status),
                            color: getStatusColor(status),
                            padding: '0.3rem 0.6rem',
                            borderRadius: '6px',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            textTransform: 'capitalize',
                            border: `1px solid ${getStatusColor(status)}20`
                          }}>
                            {status}
                          </span>
                        </div>
                      </div>

                      <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'nowrap',
                        alignItems: 'stretch',
                        gap: '0.35rem',
                        marginTop: '0.75rem',
                        width: '100%',
                        minWidth: 0,
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        paddingBottom: '2px'
                      } as React.CSSProperties}>
                        {viewMode === 'pending' && (
                          <button
                            type="button"
                            onClick={() => openSettlement(sale)}
                            style={{
                              flex: '1 1 0',
                              minWidth: '4.25rem',
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              color: 'white',
                              border: 'none',
                              padding: '0.55rem 0.3rem',
                              borderRadius: '10px',
                              fontSize: '0.68rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 3px 10px rgba(245, 158, 11, 0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.2rem',
                              whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 3px 10px rgba(245, 158, 11, 0.3)';
                            }}
                          >
                            Pay
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (!medicationsData[sale.id]) {
                              fetchMedicationsForSale(sale.id);
                            }
                            setSelectedSale(selectedSale === sale.id ? null : sale.id);
                          }}
                          style={{
                            flex: '1 1 0',
                            minWidth: '4.25rem',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '0.55rem 0.3rem',
                            borderRadius: '10px',
                            fontSize: '0.68rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 3px 10px rgba(16, 185, 129, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.2rem',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 3px 10px rgba(16, 185, 129, 0.3)';
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedSale === sale.id ? 'Hide' : 'Details'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            handlePrintInvoice(sale.id);
                          }}
                          style={{
                            flex: '1 1 0',
                            minWidth: '4.25rem',
                            background: 'linear-gradient(135deg, #FF5733 0%, #C70039 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '0.55rem 0.3rem',
                            borderRadius: '10px',
                            fontSize: '0.68rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 3px 10px rgba(255, 87, 51, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.2rem',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 87, 51, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 3px 10px rgba(255, 87, 51, 0.3)';
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14,2 14,8 20,8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                            <polyline points="10,9 9,9 8,9"/>
                          </svg>
                          Invoice
                        </button>
                      </div>
                    </div>

                    {/* Detailed View - Only shown when View button is clicked */}
                    {selectedSale === sale.id && (
                      <div style={{
                        marginTop: '1rem',
                        padding: '1rem 0.75rem',
                        background: 'rgba(248, 250, 252, 0.8)',
                        borderRadius: '12px',
                        border: '1px solid rgba(16, 185, 129, 0.1)',
                        animation: 'slideInUp 0.3s ease-out',
                        width: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box'
                      }}>
                        <h4 style={{
                          fontSize: '1rem',
                          fontWeight: '700',
                          color: '#1f2937',
                          margin: '0 0 1rem 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 12l2 2 4-4"/>
                            <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                          </svg>
                          Sale Details
                        </h4>
                        
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '1rem',
                          width: '100%',
                          minWidth: 0
                        }}>
                          {/* Customer Information */}
                          <div style={{
                            background: 'white',
                            padding: '1rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(16, 185, 129, 0.1)',
                            minWidth: 0
                          }}>
                            <h5 style={{
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              color: '#374151',
                              margin: '0 0 0.75rem 0',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>
                              Customer Information
                            </h5>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Name</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', textAlign: 'right', wordBreak: 'break-word' }}>{customerName}</span>
                              </div>
                              {customerPhone && customerPhone !== 'N/A' && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Phone</span>
                                  <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', textAlign: 'right', wordBreak: 'break-word' }}>📞 {customerPhone}</span>
                                </div>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Type</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', textTransform: 'capitalize', textAlign: 'right' }}>{customerType}</span>
                              </div>
                            </div>
                          </div>

                          {/* Invoice Information */}
                          <div style={{
                            background: 'white',
                            padding: '1rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(16, 185, 129, 0.1)',
                            minWidth: 0
                          }}>
                            <h5 style={{
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              color: '#374151',
                              margin: '0 0 0.75rem 0',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>
                              Invoice Information
                            </h5>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Invoice No</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', fontFamily: 'monospace', textAlign: 'right', wordBreak: 'break-all' }}>{invoiceNo}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Date</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', textAlign: 'right' }}>{saleDate}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Created By</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', textAlign: 'right' }}>User #{createdBy}</span>
                              </div>
                            </div>
                          </div>

                          {/* Financial Details */}
                          <div style={{
                            background: 'white',
                            padding: '1rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(16, 185, 129, 0.1)',
                            minWidth: 0
                          }}>
                            <h5 style={{
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              color: '#374151',
                              margin: '0 0 0.75rem 0',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>
                              Financial Details
                            </h5>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Total Amount</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#3b82f6', textAlign: 'right' }}>₹{(typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0).toFixed(2)}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Total Tax</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', textAlign: 'right' }}>
                                  ₹{(billSummaryData[sale.id]?.total_tax ?? (medicationsData[sale.id]?.reduce((sum, m) => sum + (m.tax_amount || 0), 0) || 0)).toFixed(2)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Total Discount</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', textAlign: 'right' }}>
                                  ₹{(billSummaryData[sale.id]?.total_discount ?? (medicationsData[sale.id]?.reduce((sum, m) => {
                                    const unit = m.unit_price ?? m.selling_price ?? m.price ?? 0;
                                    const qty = m.quantity ?? 0;
                                    if (m.discount_amount != null) return sum + (m.discount_amount || 0);
                                    const dperc = m.discount_percent || 0;
                                    return sum + ((unit * qty) * dperc / 100);
                                  }, 0) || 0)).toFixed(2)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Amount Paid</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#10b981', textAlign: 'right' }}>₹{(typeof amountPaid === 'number' ? amountPaid : parseFloat(amountPaid) || 0).toFixed(2)}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Pending Amount</span>
                                <span style={{
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  color: Math.max(0, (typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0) - (typeof amountPaid === 'number' ? amountPaid : parseFloat(amountPaid) || 0)) > 0 ? '#ef4444' : '#10b981',
                                  textAlign: 'right'
                                }}>
                                  ₹{Math.max(0, (typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0) - (typeof amountPaid === 'number' ? amountPaid : parseFloat(amountPaid) || 0)).toFixed(2)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Payment Mode</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', textTransform: 'uppercase', textAlign: 'right' }}>{paymentMode}</span>
                              </div>
                            </div>
                          </div>

                          {/* Additional Information */}
                          <div style={{
                            background: 'white',
                            padding: '1rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(16, 185, 129, 0.1)',
                            minWidth: 0
                          }}>
                            <h5 style={{
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              color: '#374151',
                              margin: '0 0 0.75rem 0',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>
                              Additional Information
                            </h5>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Status</span>
                                <span style={{
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  color: getStatusColor(status),
                                  textTransform: 'capitalize',
                                  textAlign: 'right'
                                }}>
                                  {status}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Total Quantity</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', textAlign: 'right' }}>
                                  {billSummaryData[sale.id]?.total_quantity ?? (medicationsData[sale.id]?.reduce((sum, m) => sum + (m.quantity || 0), 0) || 0)}
                                </span>
                              </div>
                              {notes && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', padding: '0.45rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>Notes</span>
                                  <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', textAlign: 'right', wordBreak: 'break-word' }}>{notes}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Medications Section */}
                          <div style={{
                            background: 'white',
                            padding: '1rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(59, 130, 246, 0.1)',
                            width: '100%',
                            minWidth: 0
                          }}>
                            <h5 style={{
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              color: '#374151',
                              margin: '0 0 0.75rem 0',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7z"/>
                              </svg>
                              Medications ({medicationsData[sale.id]?.length || 0})
                            </h5>
                            
                            {loadingMedications[sale.id] ? (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '2rem',
                                color: '#6b7280'
                              }}>
                                <div style={{
                                  width: '24px',
                                  height: '24px',
                                  border: '3px solid rgba(59, 130, 246, 0.1)',
                                  borderTop: '3px solid #3b82f6',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite',
                                  marginRight: '0.75rem'
                                }}></div>
                                Loading medications...
                              </div>
                            ) : medicationsError[sale.id] ? (
                              <div style={{
                                padding: '1rem',
                                background: 'rgba(239, 68, 68, 0.05)',
                                border: '1px solid rgba(239, 68, 68, 0.2)',
                                borderRadius: '6px',
                                color: '#ef4444',
                                fontSize: '0.85rem'
                              }}>
                                {medicationsError[sale.id]}
                              </div>
                            ) : medicationsData[sale.id] && medicationsData[sale.id].length > 0 ? (
                              <div style={{
                                display: 'grid',
                                gap: '0.75rem'
                              }}>
                                {medicationsData[sale.id].map((medication: any, index: number) => (
                                  <div key={index} style={{
                                    background: 'rgba(59, 130, 246, 0.05)',
                                    padding: '0.75rem',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(59, 130, 246, 0.1)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0,
                                    minWidth: 0
                                  }}>
                                    <div style={{
                                      fontSize: '0.85rem',
                                      fontWeight: '700',
                                      color: '#1f2937',
                                      marginBottom: '0.35rem',
                                      lineHeight: 1.3
                                    }}>
                                      {medication.medicine_name || medication.name || 'Unknown Medicine'}
                                    </div>
                                    {(medication.batch_no || medication.batch) && (
                                      <div style={{
                                        fontSize: '0.72rem',
                                        color: '#6b7280',
                                        fontFamily: 'monospace',
                                        marginBottom: '0.35rem'
                                      }}>
                                        Batch: {medication.batch_no || medication.batch}
                                      </div>
                                    )}
                                    {([
                                      ['Unit price', `₹${((medication.unit_price ?? medication.selling_price ?? medication.price ?? 0)).toFixed(2)}`, '#10b981'],
                                      ['Quantity', `${medication.quantity || 0} ${medication.quantity_type || 'pcs'}`, '#374151'],
                                      ['Tax %', `${(medication.tax_percent ?? medication.gst_rate ?? medication.gst ?? 0).toFixed(2)}%`, '#f59e0b'],
                                      ['Line total', `₹${((medication.line_total ?? ((medication.quantity || 0) * (medication.unit_price ?? medication.selling_price ?? medication.price ?? 0))) || 0).toFixed(2)}`, '#3b82f6']
                                    ] as const).map(([label, val, valColor]) => (
                                      <div
                                        key={label}
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          gap: '0.75rem',
                                          padding: '0.4rem 0',
                                          borderTop: '1px solid rgba(59, 130, 246, 0.12)'
                                        }}
                                      >
                                        <span style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600, flexShrink: 0 }}>{label}</span>
                                        <span style={{ fontSize: '0.82rem', fontWeight: '600', color: valColor, textAlign: 'right', wordBreak: 'break-word' }}>{val}</span>
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{
                                padding: '1rem',
                                background: 'rgba(156, 163, 175, 0.05)',
                                border: '1px solid rgba(156, 163, 175, 0.2)',
                                borderRadius: '6px',
                                color: '#6b7280',
                                fontSize: '0.85rem',
                                textAlign: 'center'
                              }}>
                                No medications found for this sale
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            ) : (
              /* Desktop Table Layout */
              <div style={{
                border: '2px solid #e5e7eb',
                borderRadius: '0',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                background: 'white',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0
              } as React.CSSProperties}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem',
                  margin: 0,
                  padding: 0,
                  borderSpacing: 0,
                  minWidth: '100%'
                }}>
                  <thead>
                    <tr style={{
                      background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                      borderBottom: '2px solid #d1d5db'
                    }}>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.8125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                        whiteSpace: 'nowrap',
                        minWidth: '60px'
                      }}>S.No</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.8125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                        whiteSpace: 'nowrap',
                        minWidth: '120px'
                      }}>Invoice No</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.8125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                        whiteSpace: 'nowrap',
                        minWidth: '150px'
                      }}>Customer Name</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.8125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                        whiteSpace: 'nowrap',
                        minWidth: '120px'
                      }}>Date</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.8125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                        whiteSpace: 'nowrap',
                        minWidth: '100px'
                      }}>Payment Mode</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'right',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.8125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                        whiteSpace: 'nowrap',
                        minWidth: '120px'
                      }}>Total Amount</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'right',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.8125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                        whiteSpace: 'nowrap',
                        minWidth: '120px'
                      }}>Amount Paid</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.8125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                        whiteSpace: 'nowrap',
                        minWidth: '100px'
                      }}>{viewMode === 'pending' ? 'Pending Amount' : 'Status'}</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.8125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                        whiteSpace: 'nowrap',
                        minWidth: '150px'
                      }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSales.length === 0 ? (
                      <tr>
                        <td
                          colSpan={9}
                          style={{
                            padding: '3rem 1rem',
                            textAlign: 'center',
                            color: '#6b7280'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.75rem'
                          }}>
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
                              <path d="M9 19c-5 0-9-4-9-9s4-9 9-9 9 4 9 9-4 9-9 9zM21 3l-2 2m-7 7l2 2"/>
                            </svg>
                            <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: '#6b7280' }}>
                              No sales found
                            </p>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af' }}>
                              Try adjusting your search criteria
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedSales.map((sale, index) => {
                        // Extract data with fallbacks for different API field names
                        const customerName = sale.customer_name || sale.patient_name || sale.patientName || 'Unknown Customer';
                        const customerPhone = sale.customer_phone || sale.patient_phone || sale.phone || 'N/A';
                        const invoiceNo = sale.invoice_no || sale.invoiceNo || sale.id || 'N/A';
                        const saleDate = sale.invoice_date || sale.created_at || sale.sale_date || sale.saleDate || 'N/A';
                        const status = sale.status || sale.payment_status || 'pending';
                        const totalAmount = sale.total_amount || sale.totalAmount || sale.amount || 0;
                        const amountPaid = sale.amount_paid || sale.paid_amount || sale.paidAmount || sale.paid || 0;
                        const paymentMode = sale.payment_mode || sale.paymentMode || 'cash';
                        const customerType = sale.customer_type || sale.customerType || 'individual';
                        const notes = sale.notes || '';
                        const createdBy = sale.created_by || sale.createdBy || 'N/A';

                        return (
                          <React.Fragment key={sale.id}>
                            <tr
                              style={{
                                borderBottom: '1px solid #e5e7eb',
                                background: index % 2 === 1 ? '#f9fafb' : 'white',
                                transition: 'background-color 0.15s ease',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.backgroundColor = '#ecfdf5';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.backgroundColor = index % 2 === 1 ? '#f9fafb' : 'white';
                                }
                              }}
                            >
                              <td style={{ padding: '0.75rem', textAlign: 'left', borderRight: '1px solid #f3f4f6' }}>
                                <span style={{
                                  fontWeight: '600',
                                  color: '#4b5563',
                                  background: '#f3f4f6',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '8px',
                                  display: 'inline-block',
                                  minWidth: '40px',
                                  textAlign: 'center'
                                }}>
                                  {startIndex + index + 1}
                                </span>
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'left', borderRight: '1px solid #f3f4f6' }}>
                                <span style={{
                                  background: '#f3f4f6',
                                  color: '#6b7280',
                                  padding: '0.2rem 0.4rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  fontFamily: 'monospace'
                                }}>
                                  {invoiceNo}
                                </span>
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'left', borderRight: '1px solid #f3f4f6' }}>
                                <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                                  {customerName}
                                </div>
                                {customerPhone && customerPhone !== 'N/A' && (
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                    📞 {customerPhone}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'left', borderRight: '1px solid #f3f4f6' }}>
                                {saleDate}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'left', borderRight: '1px solid #f3f4f6' }}>
                                <span style={{
                                  background: 'rgba(16, 185, 129, 0.1)',
                                  color: '#10b981',
                                  padding: '0.3rem 0.6rem',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  textTransform: 'uppercase'
                                }}>
                                  {paymentMode}
                                </span>
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', borderRight: '1px solid #f3f4f6' }}>
                                <span style={{ fontWeight: '700', color: '#3b82f6' }}>
                                  ₹{(typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0).toFixed(2)}
                                </span>
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', borderRight: '1px solid #f3f4f6' }}>
                                <span style={{ fontWeight: '700', color: '#10b981' }}>
                                  ₹{(typeof amountPaid === 'number' ? amountPaid : parseFloat(amountPaid) || 0).toFixed(2)}
                                </span>
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center', borderRight: '1px solid #f3f4f6' }}>
                                {viewMode === 'pending' ? (
                                  <span style={{
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    color: '#ef4444',
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: '700',
                                    border: '1px solid rgba(239, 68, 68, 0.2)'
                                  }}>
                                    ₹{Math.max(0, (typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0) - (typeof amountPaid === 'number' ? amountPaid : parseFloat(amountPaid) || 0)).toFixed(2)}
                                  </span>
                                ) : (
                                  <span style={{
                                    background: getStatusBgColor(status),
                                    color: getStatusColor(status),
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    textTransform: 'capitalize',
                                    border: `1px solid ${getStatusColor(status)}20`
                                  }}>
                                    {status}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center', borderRight: '1px solid #f3f4f6' }}>
                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                  {viewMode === 'pending' && (
                                    <button
                                      onClick={() => openSettlement(sale)}
                                      style={{
                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        color: 'white',
                                        border: 'none',
                                        padding: '0.5rem 0.75rem',
                                        borderRadius: '8px',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.3rem'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.3)';
                                      }}
                                    >
                                      Pay
                                    </button>
                                  )}
                                  <button
                                    onClick={() => {
                                      if (!medicationsData[sale.id]) {
                                        fetchMedicationsForSale(sale.id);
                                      }
                                      setSelectedSale(selectedSale === sale.id ? null : sale.id);
                                    }}
                                    style={{
                                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                      color: 'white',
                                      border: 'none',
                                      padding: '0.5rem 0.75rem',
                                      borderRadius: '8px',
                                      fontSize: '0.75rem',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease',
                                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.3rem'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-1px)';
                                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                                    }}
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                      <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                    {selectedSale === sale.id ? 'Hide' : 'Details'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      console.log('Invoice button clicked for sale:', sale.id);
                                      handlePrintInvoice(sale.id);
                                    }}
                                    style={{
                                      background: 'linear-gradient(135deg, #FF5733 0%, #C70039 100%)',
                                      color: 'white',
                                      border: 'none',
                                      padding: '0.5rem 0.75rem',
                                      borderRadius: '8px',
                                      fontSize: '0.75rem',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease',
                                      boxShadow: '0 2px 8px rgba(255, 87, 51, 0.3)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.3rem'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-1px)';
                                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 87, 51, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 87, 51, 0.3)';
                                    }}
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                      <polyline points="14,2 14,8 20,8"/>
                                      <line x1="16" y1="13" x2="8" y2="13"/>
                                      <line x1="16" y1="17" x2="8" y2="17"/>
                                      <polyline points="10,9 9,9 8,9"/>
                                    </svg>
                                    Invoice
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {/* Expandable Details Row */}
                            {selectedSale === sale.id && (
                              <tr>
                                <td colSpan={9} style={{ padding: '1.5rem', background: 'rgba(248, 250, 252, 0.8)', borderBottom: '1px solid #e5e7eb' }}>
                                  <div style={{
                                    padding: '1.5rem',
                                    background: 'white',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(16, 185, 129, 0.1)',
                                    animation: 'slideInUp 0.3s ease-out'
                                  }}>
                                    <h4 style={{
                                      fontSize: '1rem',
                                      fontWeight: '700',
                                      color: '#1f2937',
                                      margin: '0 0 1rem 0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem'
                                    }}>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 12l2 2 4-4"/>
                                        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                                      </svg>
                                      Sale Details
                                    </h4>
                                    
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                      gap: '1rem',
                                      marginBottom: '1rem'
                                    }}>
                                      {/* Customer Information */}
                                      <div style={{
                                        background: 'rgba(248, 250, 252, 0.8)',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(16, 185, 129, 0.1)'
                                      }}>
                                        <h5 style={{
                                          fontSize: '0.85rem',
                                          fontWeight: '600',
                                          color: '#374151',
                                          margin: '0 0 0.75rem 0',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em'
                                        }}>
                                          Customer Information
                                        </h5>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Name:</span>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937' }}>{customerName}</div>
                                          </div>
                                          {customerPhone && customerPhone !== 'N/A' && (
                                            <div>
                                              <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Phone:</span>
                                              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937' }}>📞 {customerPhone}</div>
                                            </div>
                                          )}
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Type:</span>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', textTransform: 'capitalize' }}>{customerType}</div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Invoice Information */}
                                      <div style={{
                                        background: 'rgba(248, 250, 252, 0.8)',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(16, 185, 129, 0.1)'
                                      }}>
                                        <h5 style={{
                                          fontSize: '0.85rem',
                                          fontWeight: '600',
                                          color: '#374151',
                                          margin: '0 0 0.75rem 0',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em'
                                        }}>
                                          Invoice Information
                                        </h5>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Invoice No:</span>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', fontFamily: 'monospace' }}>{invoiceNo}</div>
                                          </div>
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Date:</span>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937' }}>{saleDate}</div>
                                          </div>
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Created By:</span>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937' }}>User #{createdBy}</div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Financial Details */}
                                      <div style={{
                                        background: 'rgba(248, 250, 252, 0.8)',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(16, 185, 129, 0.1)'
                                      }}>
                                        <h5 style={{
                                          fontSize: '0.85rem',
                                          fontWeight: '600',
                                          color: '#374151',
                                          margin: '0 0 0.75rem 0',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em'
                                        }}>
                                          Financial Details
                                        </h5>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Total Amount:</span>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#3b82f6' }}>₹{(typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0).toFixed(2)}</div>
                                          </div>
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Amount Paid:</span>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#10b981' }}>₹{(typeof amountPaid === 'number' ? amountPaid : parseFloat(amountPaid) || 0).toFixed(2)}</div>
                                          </div>
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Pending Amount:</span>
                                            <div style={{ 
                                              fontSize: '0.9rem', 
                                              fontWeight: '600', 
                                              color: Math.max(0, (typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0) - (typeof amountPaid === 'number' ? amountPaid : parseFloat(amountPaid) || 0)) > 0 ? '#ef4444' : '#10b981'
                                            }}>
                                              ₹{Math.max(0, (typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0) - (typeof amountPaid === 'number' ? amountPaid : parseFloat(amountPaid) || 0)).toFixed(2)}
                                            </div>
                                          </div>
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Payment Mode:</span>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937', textTransform: 'uppercase' }}>{paymentMode}</div>
                                          </div>
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Total Tax:</span>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937' }}>
                                              ₹{(billSummaryData[sale.id]?.total_tax ?? (medicationsData[sale.id]?.reduce((sum, m) => sum + (m.tax_amount || 0), 0) || 0)).toFixed(2)}
                                            </div>
                                          </div>
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Total Discount:</span>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937' }}>
                                              ₹{(billSummaryData[sale.id]?.total_discount ?? (medicationsData[sale.id]?.reduce((sum, m) => {
                                                const unit = m.unit_price ?? m.selling_price ?? m.price ?? 0;
                                                const qty = m.quantity ?? 0;
                                                if (m.discount_amount != null) return sum + (m.discount_amount || 0);
                                                const dperc = m.discount_percent || 0;
                                                return sum + ((unit * qty) * dperc / 100);
                                              }, 0) || 0)).toFixed(2)}
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Additional Information */}
                                      <div style={{
                                        background: 'rgba(248, 250, 252, 0.8)',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(16, 185, 129, 0.1)'
                                      }}>
                                        <h5 style={{
                                          fontSize: '0.85rem',
                                          fontWeight: '600',
                                          color: '#374151',
                                          margin: '0 0 0.75rem 0',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em'
                                        }}>
                                          Additional Information
                                        </h5>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Status:</span>
                                            <div style={{ 
                                              fontSize: '0.9rem', 
                                              fontWeight: '600', 
                                              color: getStatusColor(status),
                                              textTransform: 'capitalize'
                                            }}>
                                              {status}
                                            </div>
                                          </div>
                                          <div>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Total Quantity:</span>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937' }}>
                                              {billSummaryData[sale.id]?.total_quantity ?? (medicationsData[sale.id]?.reduce((sum, m) => sum + (m.quantity || 0), 0) || 0)}
                                            </div>
                                          </div>
                                          {notes && (
                                            <div>
                                              <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Notes:</span>
                                              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1f2937' }}>{notes}</div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Medications Section */}
                                    <div style={{
                                      background: 'rgba(248, 250, 252, 0.8)',
                                      padding: '1rem',
                                      borderRadius: '8px',
                                      border: '1px solid rgba(59, 130, 246, 0.1)',
                                      marginTop: '1rem'
                                    }}>
                                      <h5 style={{
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        color: '#374151',
                                        margin: '0 0 0.75rem 0',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                      }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7z"/>
                                        </svg>
                                        Medications ({medicationsData[sale.id]?.length || 0})
                                      </h5>
                                      
                                      {loadingMedications[sale.id] ? (
                                        <div style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          padding: '2rem',
                                          color: '#6b7280'
                                        }}>
                                          <div style={{
                                            width: '24px',
                                            height: '24px',
                                            border: '3px solid rgba(59, 130, 246, 0.1)',
                                            borderTop: '3px solid #3b82f6',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite',
                                            marginRight: '0.75rem'
                                          }}></div>
                                          Loading medications...
                                        </div>
                                      ) : medicationsError[sale.id] ? (
                                        <div style={{
                                          padding: '1rem',
                                          background: 'rgba(239, 68, 68, 0.05)',
                                          border: '1px solid rgba(239, 68, 68, 0.2)',
                                          borderRadius: '6px',
                                          color: '#ef4444',
                                          fontSize: '0.85rem'
                                        }}>
                                          {medicationsError[sale.id]}
                                        </div>
                                      ) : medicationsData[sale.id] && medicationsData[sale.id].length > 0 ? (
                                        <div style={{
                                          display: 'grid',
                                          gap: '0.75rem'
                                        }}>
                                          {medicationsData[sale.id].map((medication: any, medIndex: number) => (
                                            <div key={medIndex} style={{
                                              background: 'white',
                                              padding: '0.75rem',
                                              borderRadius: '6px',
                                              border: '1px solid rgba(59, 130, 246, 0.1)',
                                              display: 'grid',
                                                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
                                              gap: '0.5rem',
                                              alignItems: 'center'
                                            }}>
                                              <div>
                                                <div style={{
                                                  fontSize: '0.8rem',
                                                  fontWeight: '600',
                                                  color: '#1f2937',
                                                  marginBottom: '0.25rem'
                                                }}>
                                                  {medication.medicine_name || medication.name || 'Unknown Medicine'}
                                                </div>
                                                {(medication.batch_no || medication.batch) && (
                                                  <div style={{
                                                    fontSize: '0.7rem',
                                                    color: '#6b7280',
                                                    fontFamily: 'monospace'
                                                  }}>
                                                    Batch: {medication.batch_no || medication.batch}
                                                  </div>
                                                )}
                                              </div>
                                                <div style={{ textAlign: 'center' }}>
                                                  <div style={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: '600',
                                                    color: '#10b981'
                                                  }}>
                                                    ₹{((medication.unit_price ?? medication.selling_price ?? medication.price ?? 0)).toFixed(2)}
                                                  </div>
                                                  <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                                    Unit Price
                                                  </div>
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                  <div style={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: '600',
                                                    color: '#374151'
                                                  }}>
                                                    {medication.quantity || 0}
                                                  </div>
                                                  <div style={{
                                                    fontSize: '0.7rem',
                                                    color: '#6b7280'
                                                  }}>
                                                    Qty
                                                  </div>
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                  <div style={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'capitalize'
                                                  }}>
                                                    {medication.quantity_type || 'pcs'}
                                                  </div>
                                                  <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                                    Type
                                                  </div>
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                  <div style={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: '600',
                                                    color: '#f59e0b'
                                                  }}>
                                                    {(medication.tax_percent ?? medication.gst_rate ?? medication.gst ?? 0).toFixed(2)}%
                                                  </div>
                                                  <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                                    Tax %
                                                  </div>
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                  <div style={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: '700',
                                                    color: '#3b82f6'
                                                  }}>
                                                    ₹{((medication.line_total ?? ((medication.quantity || 0) * (medication.unit_price ?? medication.selling_price ?? medication.price ?? 0))) || 0).toFixed(2)}
                                                  </div>
                                                  <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                                    Line Total
                                                  </div>
                                                </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div style={{
                                          padding: '1rem',
                                          background: 'rgba(156, 163, 175, 0.05)',
                                          border: '1px solid rgba(156, 163, 175, 0.2)',
                                          borderRadius: '6px',
                                          color: '#6b7280',
                                          fontSize: '0.85rem',
                                          textAlign: 'center'
                                        }}>
                                          No medications found for this sale
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls - Only show when there are results */}
            {totalItems > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '2rem',
                padding: isMobile ? '1rem' : '1.5rem',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                borderRadius: isMobile ? '16px' : '16px',
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.06)',
                gap: isMobile ? '1rem' : '0'
              }}>
                {/* Items Per Page Selector */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  justifyContent: isMobile ? 'center' : 'flex-start',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  <span style={{
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    whiteSpace: 'nowrap'
                  }}>
                    Show:
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing items per page
                    }}
                    style={{
                      padding: isMobile ? '0.625rem 0.75rem' : '0.5rem 0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      fontWeight: '500',
                      color: '#374151',
                      backgroundColor: 'white',
                      outline: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      minWidth: isMobile ? '80px' : 'auto'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                  </select>
                </div>

                {/* Page Info */}
                <div style={{
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  fontWeight: '500',
                  color: '#6b7280',
                  textAlign: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}>
                  Page {currentPage} of {totalPages} 
                  <span style={{ margin: '0 0.5rem', color: '#d1d5db' }}>•</span>
                  {totalItems > 0 ? `Showing ${startIndex + 1}-${Math.min(endIndex, totalItems)} of ${totalItems} records` : '0 records'}
                </div>

                {/* Page Navigation */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                  justifyContent: isMobile ? 'center' : 'flex-end',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  {/* Previous Button */}
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || totalPages <= 1}
                    style={{
                      padding: isMobile ? '0.625rem 0.75rem' : '0.5rem 0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      fontWeight: '600',
                      backgroundColor: (currentPage === 1 || totalPages <= 1) ? '#f9fafb' : 'white',
                      color: (currentPage === 1 || totalPages <= 1) ? '#9ca3af' : '#374151',
                      cursor: (currentPage === 1 || totalPages <= 1) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      minWidth: isMobile ? '100px' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1 && totalPages > 1) {
                        e.currentTarget.style.borderColor = '#10b981';
                        e.currentTarget.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1 && totalPages > 1) {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15,18 9,12 15,6"/>
                    </svg>
                    Previous
                  </button>

                  {/* Page Numbers */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                  }}>
                    {(() => {
                      // Generate page numbers array - similar to purchase-history
                      const pageNumbers: (number | string)[] = [];
                      const maxVisible = isMobile ? 3 : 5;
                      
                      if (totalPages <= maxVisible) {
                        // Show all pages if total is less than max visible
                        for (let i = 1; i <= totalPages; i++) {
                          pageNumbers.push(i);
                        }
                      } else {
                        // Always show first page
                        pageNumbers.push(1);
                        
                        // Calculate range around current page
                        let startPage = Math.max(2, currentPage - 1);
                        let endPage = Math.min(totalPages - 1, currentPage + 1);
                        
                        // Adjust if we're near the start
                        if (currentPage <= 2) {
                          endPage = Math.min(totalPages - 1, 3);
                        }
                        
                        // Adjust if we're near the end
                        if (currentPage >= totalPages - 1) {
                          startPage = Math.max(2, totalPages - 2);
                        }
                        
                        // Add ellipsis before range if needed
                        if (startPage > 2) {
                          pageNumbers.push('...');
                        }
                        
                        // Add pages in range
                        for (let i = startPage; i <= endPage; i++) {
                          pageNumbers.push(i);
                        }
                        
                        // Add ellipsis after range if needed
                        if (endPage < totalPages - 1) {
                          pageNumbers.push('...');
                        }
                        
                        // Always show last page
                        if (totalPages > 1) {
                          pageNumbers.push(totalPages);
                        }
                      }
                      
                      return pageNumbers.map((page, index) => {
                        if (page === '...') {
                          return (
                            <span key={`ellipsis-${index}`} style={{ color: '#9ca3af', fontSize: '0.9rem', padding: '0 0.25rem' }}>
                              ...
                            </span>
                          );
                        }
                        
                        const pageNum = page as number;
                        const isActive = pageNum === currentPage;
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            style={{
                              minWidth: isMobile ? '36px' : '40px',
                              padding: isMobile ? '0.5rem 0.625rem' : '0.5rem 0.75rem',
                              border: '2px solid',
                              borderColor: isActive ? '#10b981' : '#e5e7eb',
                              borderRadius: '8px',
                              fontSize: isMobile ? '0.8rem' : '0.9rem',
                              fontWeight: '600',
                              backgroundColor: isActive ? '#10b981' : 'white',
                              color: isActive ? 'white' : '#374151',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              textAlign: 'center'
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.borderColor = '#10b981';
                                e.currentTarget.style.backgroundColor = '#f0fdf4';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.backgroundColor = 'white';
                              }
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      });
                    })()}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages || totalPages <= 1}
                    style={{
                      padding: isMobile ? '0.625rem 0.75rem' : '0.5rem 0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      fontWeight: '600',
                      backgroundColor: (currentPage === totalPages || totalPages <= 1) ? '#f9fafb' : 'white',
                      color: (currentPage === totalPages || totalPages <= 1) ? '#9ca3af' : '#374151',
                      cursor: (currentPage === totalPages || totalPages <= 1) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      minWidth: isMobile ? '100px' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages && totalPages > 1) {
                        e.currentTarget.style.borderColor = '#10b981';
                        e.currentTarget.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages && totalPages > 1) {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    Next
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9,18 15,12 9,6"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {totalItems === 0 && !isLoadingData && !apiError && (
              <div style={{
                textAlign: 'center',
                padding: '4rem',
                color: '#6b7280'
              }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '1.5rem', opacity: 0.5 }}>
                  <path d="M9 19c-5 0-9-4-9-9s4-9 9-9 9 4 9 9-4 9-9 9zM21 3l-2 2m-7 7l2 2"/>
                </svg>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  No sales found
                </h3>
                <p style={{
                  fontSize: '1rem',
                  color: '#6b7280'
                }}>
                  Try adjusting your search criteria
                </p>
              </div>
            )}
          </section>
            </>
          )}
        </main>
      </div>

      {/* OTP Modal for WhatsApp number verification */}
      {showOtpModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10001,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }} onClick={() => {}}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            maxWidth: '360px',
            width: '100%',
            boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', color: '#111827' }}>Verify phone number</h3>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#6b7280' }}>
              Enter OTP sent to {currentWhatsAppSale?.customer_phone || currentWhatsAppSale?.patient_phone || currentWhatsAppSale?.phone || ''}
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Enter OTP"
                value={otpValue}
                onChange={e => { setOtpValue(e.target.value); setOtpError(null); }}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: `1px solid ${otpError ? '#ef4444' : '#d1d5db'}`,
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              />
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={isResendingOtp}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #3b82f6',
                  borderRadius: '6px',
                  background: isResendingOtp ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  cursor: isResendingOtp ? 'not-allowed' : 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  whiteSpace: 'nowrap'
                }}
              >
                {isResendingOtp ? 'Resending...' : 'Resend OTP'}
              </button>
            </div>
            {otpError && <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8125rem', color: '#ef4444' }}>{otpError}</p>}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => { setShowOtpModal(false); setOtpValue(''); setOtpError(null); whatsAppPendingBlobRef.current = null; setCurrentWhatsAppSale(null); setCurrentWhatsAppMobile(''); }}
                style={{
                  flex: 1,
                  padding: '0.625rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isVerifyingOtp}
                onClick={handleOtpSubmit}
                style={{
                  flex: 1,
                  padding: '0.625rem 1rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: isVerifyingOtp ? '#9ca3af' : '#10b981',
                  color: 'white',
                  cursor: isVerifyingOtp ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                {isVerifyingOtp ? 'Verifying…' : 'Verify & Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Number Input Modal */}
      {showMobileInputModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h3 style={{
              margin: '0 0 1.5rem 0',
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#1f2937',
              textAlign: 'center'
            }}>
              Enter Mobile Number
            </h3>
            
            <p style={{
              margin: '0 0 1.5rem 0',
              fontSize: '0.875rem',
              color: '#6b7280',
              textAlign: 'center',
              lineHeight: '1.5'
            }}>
              No phone number found for this customer. Please enter a mobile number to send the invoice via WhatsApp.
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151'
              }}>
                Mobile Number
              </label>
              <input
                type="tel"
                value={mobileInputValue}
                onChange={(e) => setMobileInputValue(e.target.value)}
                placeholder="Enter 10-digit mobile number"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: mobileInputError ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
              {mobileInputError && (
                <p style={{
                  margin: '0.5rem 0 0 0',
                  fontSize: '0.875rem',
                  color: '#ef4444'
                }}>
                  {mobileInputError}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button
                type="button"
                onClick={() => {
                  setShowMobileInputModal(false);
                  setMobileInputValue('');
                  setMobileInputError(null);
                  setCurrentWhatsAppSale(null);
                  setCurrentWhatsAppMobile('');
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#6b7280',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleMobileNumberSubmit}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Send WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Response Message */}
      {showWhatsAppResponseMessage && whatsAppResponseMessage && (
        <div style={{
          position: 'fixed',
          top: '2rem',
          right: '2rem',
          zIndex: 10002,
          maxWidth: '400px',
          background: whatsAppResponseMessage.includes('Failed') || whatsAppResponseMessage.includes('Error')
            ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
            : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
          border: whatsAppResponseMessage.includes('Failed') || whatsAppResponseMessage.includes('Error')
            ? '2px solid #ef4444'
            : '2px solid #10b981',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
          padding: '1rem 1.5rem',
          animation: 'slideInRight 0.3s ease-out'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flex: 1
            }}>
              <div style={{
                background: whatsAppResponseMessage.includes('Failed') || whatsAppResponseMessage.includes('Error')
                  ? 'rgba(239, 68, 68, 0.2)'
                  : 'rgba(16, 185, 129, 0.2)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {whatsAppResponseMessage.includes('Failed') || whatsAppResponseMessage.includes('Error') ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  color: whatsAppResponseMessage.includes('Failed') || whatsAppResponseMessage.includes('Error')
                    ? '#991b1b'
                    : '#065f46',
                  margin: '0 0 0.25rem 0'
                }}>
                  {whatsAppResponseMessage.includes('Failed') || whatsAppResponseMessage.includes('Error')
                    ? 'Error'
                    : 'Success'}
                </h3>
                <p style={{
                  fontSize: '0.85rem',
                  color: whatsAppResponseMessage.includes('Failed') || whatsAppResponseMessage.includes('Error')
                    ? '#991b1b'
                    : '#065f46',
                  margin: 0,
                  fontWeight: '600',
                  lineHeight: '1.4',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                }}>
                  {whatsAppResponseMessage}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowWhatsAppResponseMessage(false);
                setWhatsAppResponseMessage(null);
              }}
              style={{
                background: whatsAppResponseMessage.includes('Failed') || whatsAppResponseMessage.includes('Error')
                  ? 'rgba(239, 68, 68, 0.2)'
                  : 'rgba(16, 185, 129, 0.2)',
                border: 'none',
                borderRadius: '4px',
                padding: '0.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.background = whatsAppResponseMessage.includes('Failed') || whatsAppResponseMessage.includes('Error')
                    ? 'rgba(239, 68, 68, 0.3)'
                    : 'rgba(16, 185, 129, 0.3)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.background = whatsAppResponseMessage.includes('Failed') || whatsAppResponseMessage.includes('Error')
                    ? 'rgba(239, 68, 68, 0.2)'
                    : 'rgba(16, 185, 129, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={whatsAppResponseMessage.includes('Failed') || whatsAppResponseMessage.includes('Error') ? '#991b1b' : '#065f46'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {isSettlementOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '520px',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
            border: '1px solid rgba(16, 185, 129, 0.15)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '1rem 1.25rem',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>Complete Payment</div>
              <button onClick={closeSettlement} style={{
                background: 'transparent',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                fontSize: '1rem'
              }}>✕</button>
            </div>
            <div style={{ padding: '1.25rem', display: 'grid', gap: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: '#6b7280', fontWeight: 600 }}>Pending Amount</div>
                <div style={{ fontWeight: 800, color: '#ef4444' }}>₹{settlementPendingAmount.toFixed(2)}</div>
              </div>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 500 }}>Amount</div>
                <input
                  type="number"
                  step="0.01"
                  value={settlementAmount}
                  onChange={(e) => setSettlementAmount(e.target.value)}
                  style={{
                    padding: '0.625rem 0.875rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    backgroundColor: 'white',
                    color: '#1f2937',
                    outline: 'none'
                  }}
                />
              </div>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 500 }}>Payment Mode</div>
                <select
                  value={settlementMode}
                  onChange={(e) => setSettlementMode(e.target.value)}
                  style={{
                    padding: '0.625rem 0.875rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    backgroundColor: 'white',
                    color: '#1f2937',
                    outline: 'none'
                  }}
                >
                  <option value="upi">UPI</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                </select>
              </div>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 500 }}>Notes (optional)</div>
                <textarea
                  value={settlementNotes}
                  onChange={(e) => setSettlementNotes(e.target.value)}
                  rows={3}
                  style={{
                    padding: '0.625rem 0.875rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    backgroundColor: 'white',
                    color: '#1f2937',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
              </div>
              {settlementError && (
                <div style={{
                  padding: '0.75rem',
                  background: '#fef2f2',
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                  borderRadius: '10px',
                  fontSize: '0.9rem'
                }}>
                  {settlementError}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button
                  onClick={closeSettlement}
                  disabled={settlementLoading}
                  style={{
                    padding: '0.6rem 0.9rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    background: 'white',
                    color: '#374151',
                    cursor: settlementLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitSettlement}
                  disabled={settlementLoading}
                  style={{
                    padding: '0.6rem 0.9rem',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    cursor: settlementLoading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 3px 12px rgba(16, 185, 129, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {settlementLoading ? (
                    <div style={{
                      width: '14px',
                      height: '14px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                  ) : null}
                  Complete Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        * {
          box-sizing: border-box;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        
        input::placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input::-webkit-input-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input::-moz-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input:-ms-input-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes backgroundFloat {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @keyframes particleFloat1 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.3;
          }
          25% {
            transform: translate(20px, -30px) scale(1.2);
            opacity: 0.6;
          }
          50% {
            transform: translate(-15px, -20px) scale(0.8);
            opacity: 0.4;
          }
          75% {
            transform: translate(10px, 25px) scale(1.1);
            opacity: 0.5;
          }
        }

        @keyframes particleFloat2 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.25;
          }
          33% {
            transform: translate(-25px, 20px) scale(1.3);
            opacity: 0.5;
          }
          66% {
            transform: translate(30px, -15px) scale(0.7);
            opacity: 0.3;
          }
        }

        @keyframes particleFloat3 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.4;
          }
          20% {
            transform: translate(15px, -25px) scale(1.4);
            opacity: 0.7;
          }
          40% {
            transform: translate(-20px, 10px) scale(0.6);
            opacity: 0.2;
          }
          60% {
            transform: translate(25px, 20px) scale(1.1);
            opacity: 0.5;
          }
          80% {
            transform: translate(-10px, -15px) scale(0.9);
            opacity: 0.3;
          }
        }

        @keyframes particleFloat4 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.2;
          }
          30% {
            transform: translate(-30px, -20px) scale(1.5);
            opacity: 0.6;
          }
          60% {
            transform: translate(20px, 30px) scale(0.5);
            opacity: 0.1;
          }
        }

        @keyframes particleFloat5 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.15;
          }
          25% {
            transform: translate(35px, -25px) scale(1.6);
            opacity: 0.4;
          }
          50% {
            transform: translate(-25px, 35px) scale(0.4);
            opacity: 0.05;
          }
          75% {
            transform: translate(15px, -10px) scale(1.2);
            opacity: 0.3;
          }
        }

        @keyframes particleFloat6 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.2;
          }
          40% {
            transform: translate(-20px, -30px) scale(1.3);
            opacity: 0.5;
          }
          80% {
            transform: translate(30px, 20px) scale(0.7);
            opacity: 0.1;
          }
        }

        @keyframes shapeRotate1 {
          0% {
            transform: rotate(0deg) scale(1);
          }
          50% {
            transform: rotate(180deg) scale(1.2);
          }
          100% {
            transform: rotate(360deg) scale(1);
          }
        }

        @keyframes shapeRotate2 {
          0% {
            transform: rotate(0deg) scale(1);
          }
          50% {
            transform: rotate(-180deg) scale(0.8);
          }
          100% {
            transform: rotate(-360deg) scale(1);
          }
        }

        @keyframes shapeRotate3 {
          0% {
            transform: rotate(0deg) scale(1);
          }
          100% {
            transform: rotate(360deg) scale(1);
          }
        }

        @keyframes shapePulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.15;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.3;
          }
        }

        @keyframes shine {
          0% {
            transform: translateX(-100%) translateY(-100%) rotate(45deg);
          }
          50% {
            transform: translateX(100%) translateY(100%) rotate(45deg);
          }
          100% {
            transform: translateX(100%) translateY(100%) rotate(45deg);
          }
        }

        /* Mobile Responsive Styles for Sales History */
        @media (max-width: 768px) {
          .sales-header {
            padding: 1rem 0 !important;
            overflow-x: hidden !important;
          }

          .sales-header-container {
            flex-direction: column !important;
            gap: 1rem !important;
            padding: 0 1rem !important;
            width: 100% !important;
            max-width: 100% !important;
            align-items: stretch !important;
          }

          .sales-header-container > button {
            align-self: flex-start;
            order: 1;
          }

          .sales-header-title {
            position: static !important;
            transform: none !important;
            order: 2 !important;
            width: 100% !important;
            text-align: center !important;
            margin: 0.5rem 0 !important;
            font-size: 1rem !important;
          }

          .sales-search-container {
            order: 3 !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            margin-left: 0 !important;
            margin-top: 0.5rem !important;
          }

          .sales-search-input {
            width: 100% !important;
            max-width: 100% !important;
            padding: 0.875rem 0.875rem 0.875rem 2.5rem !important;
            padding-left: 2.5rem !important;
            font-size: 0.9rem !important;
            box-sizing: border-box !important;
          }

          .sales-search-icon {
            left: 0.875rem !important;
            width: 18px !important;
            height: 18px !important;
            z-index: 1 !important;
            pointer-events: none !important;
          }

          .sales-clear-button {
            right: 0.75rem !important;
            z-index: 2 !important;
          }
        }

        @media (max-width: 480px) {
          .sales-header {
            padding: 0.75rem 0 !important;
          }

          .sales-header-container {
            padding: 0 0.75rem !important;
            gap: 0.75rem !important;
          }

          .sales-search-container {
            margin-top: 0.5rem !important;
          }

          .sales-search-input {
            padding: 1rem 1rem 1rem 2.5rem !important;
            padding-left: 2.5rem !important;
            font-size: 1rem !important;
            border-radius: 14px !important;
            min-height: 52px !important;
          }

          .sales-search-icon {
            left: 0.75rem !important;
            width: 20px !important;
            height: 20px !important;
            z-index: 1 !important;
            pointer-events: none !important;
          }

          .sales-clear-button {
            right: 0.75rem !important;
            z-index: 2 !important;
          }

          /* Ensure action buttons are visible on mobile */
          div[style*="flex: 0 0 320px"] {
            flex: 0 0 auto !important;
            width: 100% !important;
            flex-wrap: wrap !important;
          }
        }
      `}</style>
      
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
const MemoizedSalesHistory = React.memo(SalesHistory);

MemoizedSalesHistory.displayName = 'SalesHistory';

export default MemoizedSalesHistory;
